import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { isOnline, enqueue, pendingCount, autoDrain, subscribe as subscribeQueue } from "@/lib/offlineQueue";
import { showShiftNotification, clearShiftNotification, registerNotificationHandlers } from "@/lib/shiftNotification";
import { useAuth } from "@/context/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import LogoutButton from "@/components/LogoutButton";
import ThemeToggle from "@/components/ThemeToggle";
import AmountConfirmDialog from "@/components/AmountConfirmDialog";
import UndoToast from "@/components/UndoToast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DatePickerField from "@/components/DatePickerField";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import HandoverDialog from "@/components/HandoverDialog";
import CashHandoverDialog from "@/components/CashHandoverDialog";
import {
  Camera, MapPin, LogIn, LogOut, Fuel, CircleDot, CircleCheck,
  Plus, Trash2, Banknote, CreditCard, Receipt, StickyNote, CheckCircle2,
  User as UserIcon, Loader2, Lock, Unlock, AlertCircle, ArrowRightLeft, Droplets, WifiOff, ArrowLeft,
  HandCoins, Pencil, XCircle, Clock, AlertTriangle, RefreshCw,
} from "lucide-react";

const rupee = (v) =>
  v == null ? "-" : `Rs. ${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// --- Selfie capture via getUserMedia ---
function useCamera() {
  const { t: __t } = useTranslation();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 } }, audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      return true;
    } catch (e) {
      toast.error(__t("csa.csaportal.camera_permission_denied_allow_it_and_tr"));
      return false;
    }
  }, []);
  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);
  const snap = useCallback(() => {
    const v = videoRef.current;
    if (!v) return null;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth || 480;
    canvas.height = v.videoHeight || 640;
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.75);
  }, []);
  useEffect(() => () => stop(), [stop]);
  return { videoRef, start, stop, snap };
}

export default function CsaPortal() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState(null);
  const [draftState, setDraftState] = useState(null);
  const [online, setOnline] = useState(isOnline());
  const [queueSize, setQueueSize] = useState(pendingCount());

  // Register a global handler so tapping the sticky shift notification (or
  // its "Add online txn" action) routes to the Online tab. Fired once per mount.
  useEffect(() => {
    let unsub = () => {};
    registerNotificationHandlers(nav).then((fn) => { unsub = fn; });
    return () => unsub();
  }, [nav]);

  // Watch browser online/offline + queue changes for the offline banner.
  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    const unsubQueue = subscribeQueue((items) => setQueueSize(items.length));
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      unsubQueue();
    };
  }, []);

  // Auto-drain the queue whenever the browser reconnects.
  useEffect(() => {
    const runner = async (item) => {
      // Only readings are queued today. Guard against unknown kinds.
      if (item?.meta?.kind === "reading") {
        await api.request({ url: item.url, method: item.method, data: item.body });
      }
    };
    const stop = autoDrain(runner);
    return () => stop();
  }, []);

  const refreshCurrent = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hisab/draft/current");
      setAttendance(data.attendance);
      setDraftState(data.draft);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshCurrent(); }, [refreshCurrent]);

  if (loading) return <FullscreenLoader />;
  return (
    <div className="min-h-screen bg-background">
      <Header user={user} />
      <OfflineBar online={online} queueSize={queueSize} />
      <main className="mx-auto max-w-4xl px-3 pb-24 pt-4 sm:px-4 md:px-6 md:py-6">
        {!attendance ? (
          <>
            <PendingResumeChips onResumed={refreshCurrent} />
            <CheckInView onDone={refreshCurrent} />
            <MyRecentShifts />
          </>
        ) : (
          <>
            <ShiftDashboard attendance={attendance} draft={draftState} onRefresh={refreshCurrent} />
            <MyRecentShifts />
          </>
        )}
      </main>
    </div>
  );
}

function OfflineBar({ online, queueSize }) {
  const { t: __t } = useTranslation();
  if (online && queueSize === 0) return null;
  const isOffline = !online;
  return (
    <div
      className={
        "sticky top-0 z-30 flex items-center justify-center gap-2 px-3 py-1.5 text-center text-[11px] font-semibold sm:text-xs " +
        (isOffline
          ? "bg-amber-500 text-amber-950"
          : "bg-sky-500 text-white")
      }
      data-testid={isOffline ? "offline-banner" : "syncing-banner"}
    >
      {isOffline ? (
        <>
          <WifiOff className="h-3.5 w-3.5" />
          <span>{__t("csa.csaportal.offline_your_saves_are_queued_locally_an")}</span>
          {queueSize > 0 && (
            <span className="rounded-full bg-black/20 px-1.5 py-px tabular-nums">{queueSize} pending</span>
          )}
        </>
      ) : (
        <>
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>Syncing {queueSize} pending {queueSize === 1 ? "save" : "saves"}…</span>
        </>
      )}
    </div>
  );
}

function FullscreenLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
      <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading...
    </div>
  );
}

function Header({ user }) {
  const { t: __t } = useTranslation();
  const { t } = useTranslation();
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
      <div className="mx-auto flex max-w-4xl items-center justify-between gap-2 px-3 py-2.5 md:px-6 md:py-3">
        <div className="flex min-w-0 items-center gap-2.5" data-testid="csa-portal-brand">
          <Link
            to="/csa"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary font-black text-primary-foreground shadow-sm transition hover:bg-primary/90"
            title={__t("csa.csaportal.back_to_hub")}
            data-testid="csa-portal-back-hub"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="min-w-0">
            <div className="truncate font-display text-sm font-bold text-foreground md:text-base">
              HisabX · {t("csa.portal_title")}
            </div>
            <div className="truncate text-[10px] text-muted-foreground md:text-xs">
              {user?.name} · <span className="font-semibold text-foreground/80">{user?.role}</span>
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Link
            to="/profile"
            className="rounded-md p-2 text-muted-foreground transition hover:bg-muted hover:text-foreground"
            title={__t("csa.csaportal.profile_password")}
            data-testid="csa-profile-link"
          >
            <UserIcon className="h-4 w-4" />
          </Link>
          <div className="hidden md:flex md:items-center md:gap-1">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
          <div className="flex md:hidden">
            <MobileHeaderMenu />
          </div>
          <LogoutButton where="csa-portal" />
        </div>
      </div>
    </header>
  );
}

/** Mobile-only overflow menu for language + theme so the main header stays uncluttered. */
function MobileHeaderMenu() {
  const { t: __t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded-md p-2 text-muted-foreground transition hover:bg-muted hover:text-foreground"
        aria-label={__t("csa.csaportal.more_options")}
        data-testid="csa-header-more"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/></svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-1 flex flex-col gap-1 rounded-lg border border-border bg-popover p-2 shadow-lg" data-testid="csa-header-menu">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </>
      )}
    </div>
  );
}

// -------- Check-In View --------
/**
 * Iter 86 · Pending Resume Chips
 * Shows a single-tap "Resume yesterday's Evening Hisab" amber chip on the
 * CSA home whenever a Manager has force-closed the CSA's earlier attendance
 * with `seed_resume`. Manager already blessed this → no GPS/selfie needed.
 */
function PendingResumeChips({ onResumed }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/attendance/pending-resumes");
      setItems(data.items || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resume = async (att) => {
    setBusy(att.id);
    try {
      await api.post(`/attendance/resume/${att.id}`);
      toast.success(`Resumed ${att.shift_name || "shift"} · ${att.date}`);
      onResumed?.();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume");
    } finally { setBusy(null); }
  };

  if (items.length === 0) return null;
  return (
    <div className="mb-3 space-y-2" data-testid="pending-resume-chips">
      {items.map((att) => (
        <button
          key={att.id}
          type="button"
          onClick={() => resume(att)}
          disabled={busy === att.id}
          className="flex w-full items-center gap-3 rounded-xl border-2 border-amber-400 bg-amber-50 p-3 text-left transition-all hover:border-amber-500 hover:bg-amber-100 disabled:opacity-60 dark:border-amber-700 dark:bg-amber-950/40"
          data-testid={`resume-chip-${att.id}`}
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500 text-white">
            <RefreshCw className={"h-5 w-5 " + (busy === att.id ? "animate-spin" : "")} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-amber-900 dark:text-amber-100">
              Resume {att.shift_name || "shift"} · {att.date}
            </div>
            <div className="text-[11px] text-amber-800 dark:text-amber-300">
              {att.resume_seeded_by_name} needs you to redo this hisab.
              {att.resume_reason ? ` (${att.resume_reason})` : ""}
            </div>
          </div>
          <span className="rounded-full bg-amber-600 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
            Tap to resume
          </span>
        </button>
      ))}
    </div>
  );
}


function CheckInView({ onDone }) {
  const { t: __t } = useTranslation();
  const { t } = useTranslation();
  const [shifts, setShifts] = useState([]);
  const [shiftId, setShiftId] = useState("");
  const [pumpLoc, setPumpLoc] = useState(null);
  const [attendanceMode, setAttendanceMode] = useState("STRICT");
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [selfie, setSelfie] = useState(null);
  const [camOpen, setCamOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeShifts, setActiveShifts] = useState([]); // shifts I'm already checked in for today
  // Iter 86 · "For which day?" — allow CSA/Manager to backdate the check-in
  // to yesterday when redoing a shift after an override:remove. Default "today".
  const [forDay, setForDay] = useState("today"); // "today" | "yesterday"
  const { videoRef, start, stop, snap } = useCamera();

  useEffect(() => {
    api.get("/shifts").then((r) => {
      setShifts(r.data.shifts || []);
      setPumpLoc(r.data.pump || null);
      setAttendanceMode((r.data.pump?.attendance_mode || "STRICT").toUpperCase());
      if (r.data.shifts?.length) setShiftId(r.data.shifts[0].id);
    }).catch(() => {});
    // Track shifts already checked-in (in case CSA closed the tab without checking out)
    api.get("/attendance/active").then((r) => {
      if (r.data.attendance) setActiveShifts([r.data.attendance.shift_id]);
    }).catch(() => {});
  }, []);

  const alreadyActive = activeShifts.includes(shiftId);
  const attendanceOff = attendanceMode === "OFF";

  const getLocation = () => {
    setLocating(true);
    if (!navigator.geolocation) {
      toast.error(__t("csa.csaportal.geolocation_not_supported_on_this_device"));
      setLocating(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy });
        setLocating(false);
      },
      (err) => {
        toast.error(`Location error: ${err.message}`);
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  const openCam = async () => {
    setCamOpen(true);
    setTimeout(() => start(), 200);
  };
  const closeCam = () => { stop(); setCamOpen(false); };
  const capture = () => {
    const d = snap();
    if (d) setSelfie(d);
    stop(); // release camera stream immediately after capture
    setCamOpen(false);
  };

  const checkIn = async () => {
    if (!shiftId) return toast.error(__t("csa.csaportal.pick_a_shift"));
    if (!attendanceOff) {
      if (!coords) return toast.error(__t("csa.csaportal.capture_your_location_first"));
      if (!selfie) return toast.error(__t("csa.csaportal.take_a_selfie_first"));
    }
    setSubmitting(true);
    try {
      const payload = { shift_id: shiftId };
      if (!attendanceOff) {
        payload.latitude = coords.lat;
        payload.longitude = coords.lng;
        payload.selfie_base64 = selfie;
      }
      // Iter 86 · Backdated check-in — only add for_date when explicitly
      // "yesterday" so the server keeps its default (shift_logical_date)
      // for every other case.
      if (forDay === "yesterday") {
        const ist = new Date(Date.now() + 5.5 * 3600_000);
        const y = new Date(ist);
        y.setUTCDate(y.getUTCDate() - 1);
        payload.for_date = y.toISOString().slice(0, 10);
      }
      await api.post("/attendance/check-in", payload);
      // Fire a sticky Android notification so the CSA can quickly hop back
      // into the app to log online transactions. Silent no-op on web.
      const shiftObj = shifts.find((s) => s.id === shiftId);
      // Compute shift end UTC from IST HH:MM on today's IST date
      let shiftEndAt = null;
      try {
        if (shiftObj?.end_time && shiftObj?.start_time) {
          const now = new Date();
          const ist = new Date(now.getTime() + 5.5 * 3600_000);
          const [eh, em] = shiftObj.end_time.split(":").map(Number);
          const [sh, sm] = shiftObj.start_time.split(":").map(Number);
          const endIst = new Date(Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate(), eh, em, 0));
          if ((sh * 60 + sm) > (eh * 60 + em)) endIst.setUTCDate(endIst.getUTCDate() + 1); // overnight
          shiftEndAt = new Date(endIst.getTime() - 5.5 * 3600_000).toISOString();
        }
      } catch { /* fallback: no reminders scheduled */ }
      showShiftNotification({
        shiftName: shiftObj?.name || "Active",
        pumpName: pumpLoc?.name,
        shiftEndAt,
      }).catch(() => {});
      toast.success(__t("csa.csaportal.checked_in_now_allocate_your_nozzles"));
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const distanceStr = coords && pumpLoc?.latitude
    ? `~${haversine(coords.lat, coords.lng, pumpLoc.latitude, pumpLoc.longitude).toFixed(0)}m from pump`
    : null;

  return (
    <Card className="border-border shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/10 text-amber-500">
            <LogIn className="h-5 w-5" />
          </div>
          {t("csa.checkin")}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t("csa.select_shift")}</p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-1.5">
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("csa.shift")}</Label>
          {shifts.length === 0 ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
              No shifts configured. Ask your dealer to set up shifts in Pump Setup.
            </div>
          ) : (
            <Select value={shiftId} onValueChange={setShiftId}>
              <SelectTrigger data-testid="checkin-shift" className="h-12 text-base"><SelectValue /></SelectTrigger>
              <SelectContent>
                {shifts.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name} · {s.start_time} – {s.end_time}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Iter 86 · Backdated check-in toggle — hidden by default; shown as
            a compact two-button picker so it doesn't clutter the normal flow. */}
        <div className="space-y-1.5" data-testid="checkin-forday-block">
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            For which day?
          </Label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setForDay("today")}
              data-testid="checkin-forday-today"
              className={
                "rounded-lg border-2 px-3 py-2 text-sm font-semibold transition-colors " +
                (forDay === "today"
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border bg-card text-muted-foreground hover:text-foreground")
              }
            >
              Today (auto)
            </button>
            <button
              type="button"
              onClick={() => setForDay("yesterday")}
              data-testid="checkin-forday-yesterday"
              className={
                "rounded-lg border-2 px-3 py-2 text-sm font-semibold transition-colors " +
                (forDay === "yesterday"
                  ? "border-amber-500 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
                  : "border-border bg-card text-muted-foreground hover:text-foreground")
              }
            >
              Yesterday's shift
            </button>
          </div>
          {forDay === "yesterday" && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-[11px] leading-snug text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200" data-testid="checkin-forday-hint">
              This shift will be recorded on yesterday's date. Use only when a manager removed your earlier check-in and you're redoing that shift.
            </div>
          )}
        </div>

        <div className={attendanceOff ? "hidden" : "grid gap-3 sm:grid-cols-2"}>
          <div className="rounded-xl border-2 border-border bg-card p-3.5 transition-colors hover:border-primary/30">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{__t("setup.location")}</div>
              <Button size="sm" variant="outline" onClick={getLocation} disabled={locating} data-testid="checkin-location" className="h-8">
                <MapPin className="h-3.5 w-3.5" /> {locating ? "Locating…" : coords ? "Refresh" : "Capture"}
              </Button>
            </div>
            {coords ? (
              <div className="space-y-0.5 font-mono text-[11px] tabular-nums text-foreground/80">
                <div>{__t("csa.csaportal.lat")}<b>{coords.lat.toFixed(6)}</b></div>
                <div>{__t("csa.csaportal.lng")}<b>{coords.lng.toFixed(6)}</b></div>
                {distanceStr && <div className="mt-1 font-sans font-semibold text-amber-700 dark:text-amber-400">{distanceStr}</div>}
              </div>
            ) : (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground/70">
                <MapPin className="h-3.5 w-3.5" /> Tap to share GPS
              </div>
            )}
          </div>

          <div className="rounded-xl border-2 border-border bg-card p-3.5 transition-colors hover:border-primary/30">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{__t("csa.csaportal.selfie")}</div>
              <Button size="sm" variant="outline" onClick={openCam} data-testid="checkin-open-cam" className="h-8">
                <Camera className="h-3.5 w-3.5" /> {selfie ? "Retake" : "Take selfie"}
              </Button>
            </div>
            {selfie ? (
              <img src={selfie} alt="selfie" className="mx-auto h-32 w-32 rounded-lg object-cover shadow-sm" />
            ) : (
              <div className="flex h-32 items-center justify-center rounded-lg bg-secondary text-xs text-muted-foreground/70">
                <Camera className="mr-1.5 h-4 w-4" /> No photo yet
              </div>
            )}
          </div>
        </div>

        {attendanceOff && (
          <div className="flex items-start gap-2.5 rounded-xl border-2 border-emerald-300 bg-emerald-50 p-3.5 dark:border-emerald-900/60 dark:bg-emerald-950/40" data-testid="attendance-off-hint">
            <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <div className="text-xs text-emerald-900 dark:text-emerald-100">
              <div className="font-bold">{__t("csa.csaportal.simple_check_in_enabled")}</div>
              <div className="mt-0.5 opacity-90">Your dealer has disabled the selfie + GPS check-in. Just pick your shift and tap Start.</div>
            </div>
          </div>
        )}

        <Button
          className="h-14 w-full text-base font-bold shadow-md shadow-primary/25"
          onClick={checkIn}
          disabled={submitting || !shiftId || alreadyActive || (!attendanceOff && (!coords || !selfie))}
          data-testid="checkin-submit"
        >
          {submitting ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> {t("common.loading")}</>
            : alreadyActive ? t("csa.checkin_active")
            : (attendanceOff ? "Start shift" : t("csa.checkin"))}
        </Button>
        {alreadyActive && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100" data-testid="already-active-hint">
            {t("csa.checkin_active")}
          </div>
        )}
      </CardContent>

      <Dialog open={camOpen} onOpenChange={(o) => !o && closeCam()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{__t("csa.csaportal.take_selfie")}</DialogTitle></DialogHeader>
          <video ref={videoRef} autoPlay playsInline className="w-full rounded-lg bg-black" />
          <DialogFooter>
            <Button variant="outline" onClick={closeCam}>{__t("common.cancel")}</Button>
            <Button onClick={capture} data-testid="checkin-capture"><Camera className="h-4 w-4" /> Capture</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function MyRecentShifts() {
  const { t: __t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (fromDate) params.set("from_date", fromDate);
    if (toDate) params.set("to_date", toDate);
    params.set("limit", "30");
    api.get(`/hisab-history?${params.toString()}`)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [fromDate, toDate]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card className="mt-6 border-border" data-testid="my-recent-shifts">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base">{__t("csa.csaportal.my_past_shifts")}</CardTitle>
            <p className="text-xs text-muted-foreground">All submitted HISABs with manager remarks + overrides. Download PDFs to share.</p>
          </div>
          <div className="flex items-center gap-1 text-[11px]">
            <DatePickerField
              value={fromDate}
              onChange={setFromDate}
              className="h-8 w-40"
              testId="history-from-date"
              ariaLabel="History from date"
              placeholder="From"
            />
            <span className="text-muted-foreground">to</span>
            <DatePickerField
              value={toDate}
              onChange={setToDate}
              min={fromDate}
              className="h-8 w-40"
              testId="history-to-date"
              ariaLabel="History to date"
              placeholder="To"
            />
            {(fromDate || toDate) && (
              <button
                type="button"
                onClick={() => { setFromDate(""); setToDate(""); }}
                className="rounded px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground hover:text-foreground"
                data-testid="history-clear-filter"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading past shifts…
          </div>
        ) : items.length === 0 ? (
          <div className="py-6 text-center text-xs text-muted-foreground">{__t("csa.csaportal.no_submitted_shifts_in_this_range_yet")}</div>
        ) : (
          <div className="divide-y divide-border">
            {items.map((h) => {
              const short = h.totals?.shortage_surplus ?? 0;
              const isOpen = expanded === h.id;
              const hasOverrides = (h.override_count || 0) > 0;
              const hasNotes = (h.flagged_notes || []).length > 0 || (h.verification_reason || "").trim().length > 0;
              return (
                <div key={h.id} className="py-2 text-sm" data-testid={`my-shift-${h.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5 text-sm">
                        <span className="font-semibold text-foreground">{h.date}</span>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-muted-foreground">{h.shift_name || h.shift_id?.replace("sh-", "") || "shift"}</span>
                        <span className={
                          "ml-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase " + (
                            h.status === "APPROVED" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300"
                            : h.status === "REJECTED" ? "bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300"
                            : "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200"
                          )
                        }>{h.status}</span>
                        {hasOverrides && (
                          <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-amber-800 dark:bg-amber-950/50 dark:text-amber-200" title={__t("csa.csaportal.manager_overrode_value_s_on_this_hisab")}>
                            {h.override_count} override{h.override_count === 1 ? "" : "s"}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {h.totals?.sales != null && `Sales ${rupee(h.totals.sales)}`}
                        {short !== 0 && (
                          <span className={short < 0 ? " text-red-600" : " text-emerald-600"}>
                            {" · "}{short < 0 ? "shortage " : "surplus "}
                            {rupee(Math.abs(short))}
                          </span>
                        )}
                        {h.verified_by_name && ` · verified by ${h.verified_by_name}`}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {(hasOverrides || hasNotes) && (
                        <button
                          type="button"
                          onClick={() => setExpanded(isOpen ? null : h.id)}
                          className="rounded border border-border bg-background px-2 py-1 text-[10px] font-semibold uppercase text-foreground/80 hover:border-primary/50 hover:text-primary"
                          data-testid={`my-shift-expand-${h.id}`}
                        >
                          {isOpen ? "Hide" : "Remarks"}
                        </button>
                      )}
                      <Button asChild variant="outline" size="sm" data-testid={`pdf-${h.id}`}>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/hisab/${h.id}/pdf`} target="_blank" rel="noreferrer">{__t("manager.pdf")}</a>
                      </Button>
                    </div>
                  </div>
                  {isOpen && (hasOverrides || hasNotes) && (
                    <div className="mt-2 space-y-1 rounded-md border border-amber-200 bg-amber-50/60 p-2 text-[11px] text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100">
                      {(h.verification_reason || "").trim() && (
                        <div><span className="font-semibold">{__t("csa.csaportal.manager_note")}</span> {h.verification_reason}</div>
                      )}
                      {(h.flagged_notes || []).map((n, i) => (
                        <div key={i}><span className="font-semibold uppercase text-[9px]">{n.section}</span> · {n.note}</div>
                      ))}
                      {(h.manager_overrides || []).map((ov, i) => (
                        <div key={`ov-${i}`} className="border-t border-amber-200/60 pt-1 dark:border-amber-800/40">
                          <span className="font-semibold uppercase text-[9px]">{ov.kind} · {ov.field}</span>
                          {" — "}<span className="line-through text-muted-foreground">{Number(ov.original ?? 0).toLocaleString("en-IN")}</span>
                          {" → "}<b className="font-mono">{Number(ov.new ?? 0).toLocaleString("en-IN")}</b>
                          <div className="text-[10px] italic text-amber-800/90 dark:text-amber-200/80">"{ov.reason}" — by {ov.actor_name || "manager"}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function haversine(lat1, lon1, lat2, lon2) {
  const r = 6371000, toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1))*Math.cos(toRad(lat2))*Math.sin(dLon/2)**2;
  return 2 * r * Math.asin(Math.sqrt(a));
}

// -------- Shift Dashboard --------
function ShiftDashboard({ attendance, draft, onRefresh }) {
  const { t: __t } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();
  const [nozzles, setNozzles] = useState([]);
  const [summary, setSummary] = useState(null);
  // Read initial tab from URL params so app-shortcut deep-links open the right tab.
  // hisabx://csa?tab=online → land on Online tab, ?tab=close → Close tab, etc.
  const initialTab = (() => {
    const t = new URLSearchParams(location.search).get("tab");
    return ["readings", "online", "lube", "credit", "expenses", "notes", "close"].includes(t) ? t : "readings";
  })();
  const [tab, setTab] = useState(initialTab);
  const [pumpConfig, setPumpConfig] = useState(null);
  const [submittedHisabId, setSubmittedHisabId] = useState(null);

  // React to URL param changes AFTER mount (e.g. shortcut fired while app already open)
  useEffect(() => {
    const t = new URLSearchParams(location.search).get("tab");
    if (t && ["readings", "online", "lube", "credit", "expenses", "notes", "close"].includes(t)) {
      setTab(t);
    }
  }, [location.search]);

  const loadStatus = useCallback(async () => {
    try {
      // On the reopened-rejection path the attendance may belong to an older
      // date, so we MUST pass date=attendance.date to /nozzles/status — otherwise
      // it defaults to today() and the CSA sees zero nozzles allocated.
      const nzUrl = attendance?.reopened && attendance?.date
        ? `/nozzles/status?shift_id=${attendance.shift_id}&date=${attendance.date}`
        : `/nozzles/status?shift_id=${attendance.shift_id}`;
      const [nz, sm] = await Promise.all([
        api.get(nzUrl),
        api.get("/hisab/draft/summary"),
      ]);
      setNozzles(nz.data.nozzles);
      setSummary(sm.data);
    } catch (e) { /* ignore */ }
  }, [attendance.shift_id, attendance?.reopened, attendance?.date]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Load pump config once — needed for the OnlineTab's list of accepted payment methods
  useEffect(() => {
    api.get("/auth/me").then((r) => setPumpConfig(r.data.pump || null)).catch(() => {});
  }, []);

  const myAllocs = summary?.allocations || [];
  const myAllocIds = new Set(myAllocs.map((a) => a.nozzle_id));
  const frozen = summary?.draft?.nozzles_frozen === true;

  // Iter 86p · When the manager unlocks the CSA's nozzles, `unfrozen_at` gets
  // stamped on the draft. Show a one-shot banner (dismissed via localStorage
  // keyed by the unfrozen_at value so it doesn't come back after acknowledge).
  const unfrozenAt = summary?.draft?.unfrozen_at || null;
  const unfrozenBy = summary?.draft?.unfrozen_by_name || summary?.draft?.unfrozen_by || null;
  const [unlockBannerVisible, setUnlockBannerVisible] = useState(false);
  useEffect(() => {
    if (!unfrozenAt || frozen) { setUnlockBannerVisible(false); return; }
    try {
      const key = "hisabx.csa.unlockAck." + unfrozenAt;
      if (localStorage.getItem(key) === "1") { setUnlockBannerVisible(false); return; }
    } catch { /* ignore */ }
    setUnlockBannerVisible(true);
  }, [unfrozenAt, frozen]);
  const dismissUnlockBanner = () => {
    try { localStorage.setItem("hisabx.csa.unlockAck." + unfrozenAt, "1"); } catch { /* ignore */ }
    setUnlockBannerVisible(false);
  };

  const toggleAllocate = async (nozzle) => {
    if (frozen) {
      toast.error(__t("csa.csaportal.nozzle_allocations_are_frozen_ask_your_m"));
      return;
    }
    try {
      if (myAllocIds.has(nozzle.id)) {
        await api.post("/nozzles/release", { nozzle_ids: [nozzle.id] });
      } else {
        // Iter 69 · Delink — pass shift_id so the server can silently
        // create attendance if the salesman skipped clock-in.
        await api.post("/nozzles/allocate", { nozzle_ids: [nozzle.id], shift_id: attendance?.shift_id });
      }
      loadStatus();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const [freezeConfirmOpen, setFreezeConfirmOpen] = useState(false);
  const freezeNozzles = async () => {
    if (myAllocs.length === 0) return toast.error(__t("csa.csaportal.allocate_at_least_one_nozzle_first"));
    try {
      await api.post("/nozzles/freeze");
      toast.success(__t("csa.csaportal.nozzles_frozen_for_this_shift"));
      setFreezeConfirmOpen(false);
      loadStatus();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6">
      {submittedHisabId ? (
        <ShiftSubmittedLock
          hisabId={submittedHisabId}
          shiftName={attendance.shift_name}
          onDone={() => { onRefresh(); nav("/"); }}
        />
      ) : (
      <>
      <Card className="border-border">
        <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{__t("csa.csaportal.active_shift")}</div>
            <div className="mt-0.5 text-lg font-bold text-foreground md:text-xl">{attendance.shift_name}</div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              Checked in {new Date(attendance.checked_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              {attendance.distance_m != null && ` · ${Math.round(attendance.distance_m)}m from pump`}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {frozen ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" data-testid="frozen-badge">
                <Lock className="h-3.5 w-3.5" /> Nozzles frozen
              </span>
            ) : null}
            <Button variant="outline" onClick={() => setTab("close")} data-testid="btn-close-shift">
              <LogOut className="h-4 w-4" /> Close shift
            </Button>
          </div>
        </CardContent>
      </Card>

      <ShiftDeadlineBar deadline={summary?.close_deadline} />

      {/* Testing Notes 15 · Rate-change banner. Polls /rates/active-changes
          and forces the CSA to capture a mid-shift meter reading before the
          shift can be submitted. */}
      <RateChangeBanner onSplitCaptured={loadStatus} />

      {attendance.reopened && (
        <div
          className="flex flex-col gap-2 rounded-2xl border-2 border-amber-400 bg-amber-50/80 p-3.5 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100 md:flex-row md:items-center md:justify-between"
          data-testid="csa-reopen-banner"
        >
          <div className="min-w-0 text-sm">
            <div className="font-bold">{__t("csa.csaportal.fixing_a_rejected_hisab")}</div>
            <div className="text-[12px] opacity-80">
              Edit your readings/entries below and resubmit. This will send an updated HISAB back to your manager for review.
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setTab("close")} data-testid="btn-goto-resubmit">
            <LogOut className="h-4 w-4" /> Resubmit
          </Button>
        </div>
      )}

      <NozzleGrid nozzles={nozzles} mineIds={myAllocIds} onToggle={toggleAllocate} frozen={frozen} />

      {unlockBannerVisible && (
        <div
          className="flex flex-col gap-2 rounded-2xl border-2 border-sky-400 bg-sky-50/90 p-3.5 text-sky-900 dark:border-sky-800 dark:bg-sky-950/50 dark:text-sky-100 md:flex-row md:items-center md:justify-between animate-in fade-in slide-in-from-top-2"
          data-testid="csa-unlock-banner"
        >
          <div className="min-w-0 text-sm">
            <div className="flex items-center gap-1.5 font-bold">
              <Unlock className="h-4 w-4" /> Manager unlocked your nozzles
            </div>
            <div className="text-[12px] opacity-85">
              {unfrozenBy ? <>Unlocked by <b>{unfrozenBy}</b>. </> : null}
              You can now add, remove, or replace your nozzles below. Freeze again once you have the right set.
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={dismissUnlockBanner}
            data-testid="csa-unlock-banner-dismiss"
            className="border-sky-400 text-sky-900 hover:bg-sky-100 dark:border-sky-700 dark:text-sky-100 dark:hover:bg-sky-900/60"
          >
            Got it
          </Button>
        </div>
      )}

      {/* ============ Freeze action row — sits directly under the live nozzle grid ============ */}
      {!frozen && (
        <div
          className="rounded-2xl border border-amber-300/70 bg-amber-50/60 p-3.5 dark:border-amber-900 dark:bg-amber-950/30"
          data-testid="freeze-action-row"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-bold text-amber-900 dark:text-amber-200">
                Done selecting nozzles?
              </div>
              <div className="mt-0.5 text-[11px] text-amber-800 dark:text-amber-300">
                Freeze locks your {myAllocs.length || "selected"} nozzle{myAllocs.length === 1 ? "" : "s"} for this shift so no one can accidentally change them mid-shift.
              </div>
            </div>
            <Button
              onClick={() => setFreezeConfirmOpen(true)}
              disabled={myAllocs.length === 0}
              className="shrink-0"
              data-testid="btn-freeze-nozzles"
            >
              <Lock className="h-4 w-4" /> Review & freeze
            </Button>
          </div>
        </div>
      )}

      <Card className="border-border">
        <CardHeader className="pb-2 pt-3 sm:pt-4">
          <div className="-mx-3 overflow-x-auto px-3 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex min-w-max gap-1.5 sm:gap-2">
              {[
                ["readings", "Readings", Fuel],
                ["online", "Online", Banknote],
                ["lube", "Lube", Droplets],
                ["credit", "Credit", CreditCard],
                ["expenses", "Expenses", Receipt],
                ["cash", "Cash", HandCoins],
                ["notes", "Notes", StickyNote],
                ["close", "Close", CheckCircle2],
              ].map(([key, label, Icon]) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  data-testid={`tab-${key}`}
                  className={"flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold transition-all sm:px-4 " +
                    (tab === key
                      ? "bg-primary text-primary-foreground shadow-sm shadow-primary/30"
                      : "bg-secondary text-muted-foreground hover:bg-secondary/80 hover:text-foreground")}
                >
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-3 sm:pt-4">
          {/* Tabs stay mounted (hidden via CSS) so switching is instant — no re-mount cost. */}
          <div className={tab === "readings" ? "" : "hidden"}>
            <ReadingsTab allocs={myAllocs} onReload={loadStatus} />
          </div>
          <div className={tab === "online" ? "" : "hidden"}>
            <OnlineTab draft={summary?.draft} onReload={loadStatus} pump={pumpConfig} />
          </div>
          <div className={tab === "lube" ? "" : "hidden"}>
            <LubeTab draft={summary?.draft} onReload={loadStatus} />
          </div>
          <div className={tab === "credit" ? "" : "hidden"}>
            <CreditTab draft={summary?.draft} onReload={loadStatus} pump={pumpConfig} />
          </div>
          <div className={tab === "expenses" ? "" : "hidden"}>
            <ExpensesTab draft={summary?.draft} onReload={loadStatus} />
          </div>
          <div className={tab === "cash" ? "" : "hidden"}>
            <CashHandoverTab onReload={loadStatus} />
          </div>
          <div className={tab === "notes" ? "" : "hidden"}>
            <NotesTab draft={summary?.draft} onReload={loadStatus} />
          </div>
          {/* Close tab still lazy — mounts only when the CSA is ready to submit */}
          {tab === "close" && <CloseTab summary={summary} onSubmitted={(id) => setSubmittedHisabId(id)} />}
        </CardContent>
      </Card>
      </>
      )}
      <Dialog open={freezeConfirmOpen} onOpenChange={setFreezeConfirmOpen}>
        <DialogContent data-testid="freeze-confirm-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-amber-600" />
              Review before freezing {myAllocs.length} nozzle{myAllocs.length === 1 ? "" : "s"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-[12px] text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
              Check the opening readings below. <b>{__t("csa.csaportal.once_frozen_you_can_t_add_or_release_noz")}</b> — only your manager can unfreeze.
            </div>

            {myAllocs.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border bg-secondary/30 p-4 text-center text-xs text-muted-foreground">
                No nozzles selected.
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-border" data-testid="freeze-summary-table">
                <div className="grid grid-cols-[1fr_auto_auto] gap-2 border-b border-border bg-secondary/60 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  <div>{__t("csa.nozzle")}</div>
                  <div className="text-right">{__t("csa.opening")}</div>
                  <div className="text-right">{__t("csa.rate")}</div>
                </div>
                <div className="max-h-64 divide-y divide-border overflow-y-auto bg-background">
                  {myAllocs.map((a) => (
                    <div key={a.id} className="grid grid-cols-[1fr_auto_auto] gap-2 px-3 py-2 text-[13px]" data-testid={`freeze-row-${a.id}`}>
                      <div className="min-w-0">
                        <div className="font-bold text-foreground">
                          {a.machine_code}·{a.nozzle_code}
                        </div>
                        <div className="truncate text-[10.5px] text-muted-foreground">
                          {a.product_name || "—"}
                        </div>
                      </div>
                      <div className="text-right font-mono tabular-nums">
                        {a.opening != null ? Number(a.opening).toFixed(2) : <span className="text-red-600">missing</span>}
                        <div className="text-[9px] uppercase text-muted-foreground">litres</div>
                      </div>
                      <div className="text-right font-mono tabular-nums text-muted-foreground">
                        ₹{a.rate != null ? Number(a.rate).toFixed(2) : "—"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {myAllocs.some((a) => a.opening == null) && (
              <div className="rounded-lg border border-red-300 bg-red-50/60 p-2.5 text-[11px] text-red-900 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200" data-testid="freeze-missing-openings">
                Some nozzles are missing an opening reading. You can still freeze, but enter them in the Readings tab before shift close.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFreezeConfirmOpen(false)} data-testid="freeze-cancel">{__t("common.cancel")}</Button>
            <Button onClick={freezeNozzles} disabled={myAllocs.length === 0} data-testid="freeze-confirm-btn">
              <Lock className="h-4 w-4" /> Yes, freeze these
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
/**
 * RateChangeBanner — Testing Notes 15
 * Polls `/api/rates/active-changes` for rate changes that occurred DURING
 * this CSA's active shift. Shows a red banner if any nozzle still needs a
 * mid-shift meter reading, opens a dialog to capture readings one nozzle
 * at a time, and calls the parent's onSplitCaptured callback so the
 * shift summary re-renders.
 */
function RateChangeBanner({ onSplitCaptured }) {
  const [active, setActive] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null); // one change entry

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/rates/active-changes");
      setActive(data.active || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60_000); // poll every minute
    return () => clearInterval(t);
  }, [refresh]);

  const needsAny = active.some((a) => (a.nozzles || []).some((n) => n.needs_split));
  if (!needsAny) return null;

  const flat = active.flatMap((a) =>
    (a.changes || []).map((c) => ({
      shift_id: a.shift_id, change: c,
      nozzles: (a.nozzles || []).filter((n) => n.needs_split),
    }))
  );

  return (
    <>
      <div
        className="rounded-2xl border-2 border-rose-400 bg-rose-50 p-4 dark:bg-rose-950/30 dark:border-rose-800"
        data-testid="rate-change-banner"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-rose-900 dark:text-rose-100">
              Rate changed during your shift
            </div>
            <div className="mt-0.5 text-[12px] text-rose-800 dark:text-rose-200">
              Take a mid-shift meter reading NOW for each nozzle below. Without this,
              your HISAB will be blocked from submitting.
            </div>
            <ul className="mt-2 space-y-1 text-[11px] text-rose-900 dark:text-rose-100">
              {flat.map((f, i) => (
                <li key={i} className="rounded-md bg-white/60 px-2 py-1 dark:bg-rose-900/40">
                  <b>{f.change.product}</b> · new rate ₹{Number(f.change.rate).toFixed(2)} effective{" "}
                  {new Date(f.change.effective_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  {f.nozzles.length > 0 && (
                    <> · <b>{f.nozzles.length}</b> nozzle{f.nozzles.length === 1 ? "" : "s"} pending</>
                  )}
                </li>
              ))}
            </ul>
            <div className="mt-3">
              <Button
                onClick={() => {
                  setSelected(flat[0]);
                  setDialogOpen(true);
                }}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="rate-change-capture-btn"
              >
                Capture split readings
              </Button>
            </div>
          </div>
        </div>
      </div>
      <SplitReadingDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        entry={selected}
        onDone={() => {
          refresh();
          onSplitCaptured && onSplitCaptured();
        }}
      />
    </>
  );
}

function SplitReadingDialog({ open, onOpenChange, entry, onDone }) {
  const [readings, setReadings] = useState({});
  const [testing, setTesting] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setReadings({}); setTesting({}); }, [entry]);
  if (!entry) return null;
  const nozzles = entry.nozzles || [];

  const saveAll = async () => {
    setSaving(true);
    try {
      for (const n of nozzles) {
        const v = readings[n.id];
        if (v == null || v === "") {
          toast.error(`Enter reading for ${n.machine_code}·${n.nozzle_code}`);
          setSaving(false); return;
        }
        await api.post("/hisab/draft/split-reading", {
          nozzle_allocation_id: n.id,
          at_ts: entry.change.effective_at,
          meter_reading: Number(v),
          testing_before: Number(testing[n.id] || 0),
          note: `Rate change: ${entry.change.product} → ₹${entry.change.rate}`,
        });
      }
      toast.success("Split readings saved");
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="split-reading-dialog">
        <DialogHeader>
          <DialogTitle>Mid-shift meter reading</DialogTitle>
          <p className="text-[11px] text-muted-foreground">
            Rate for <b>{entry.change.product}</b> changed to ₹{Number(entry.change.rate).toFixed(2)} at{" "}
            {new Date(entry.change.effective_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.
            Enter the meter reading you took at that moment.
          </p>
        </DialogHeader>
        <div className="space-y-3 max-h-[55vh] overflow-y-auto">
          {nozzles.map((n) => (
            <div key={n.id} className="rounded-lg border border-border p-3" data-testid={`split-nozzle-${n.id}`}>
              <div className="text-sm font-semibold">
                {n.machine_code}·{n.nozzle_code} <span className="text-[11px] text-muted-foreground">· {n.product}</span>
              </div>
              <div className="mt-1 text-[10px] text-muted-foreground">
                Opening: <b>{n.opening?.toFixed?.(2) ?? n.opening}</b>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase text-muted-foreground">Meter reading now</label>
                  <Input
                    type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*"
                    value={readings[n.id] || ""}
                    onChange={(e) => setReadings({ ...readings, [n.id]: e.target.value.replace(/[^0-9.]/g, "") })}
                    placeholder="e.g. 5150.50"
                    data-testid={`split-reading-${n.id}`}
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase text-muted-foreground">Testing L (pre-change)</label>
                  <Input
                    type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*"
                    value={testing[n.id] || ""}
                    onChange={(e) => setTesting({ ...testing, [n.id]: e.target.value.replace(/[^0-9.]/g, "") })}
                    placeholder="0"
                    data-testid={`split-testing-${n.id}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="split-cancel">
            Cancel
          </Button>
          <Button onClick={saveAll} disabled={saving} data-testid="split-save">
            {saving ? "Saving…" : `Save ${nozzles.length} reading${nozzles.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ShiftSubmittedLock({ hisabId, shiftName, onDone }) {
  return (
    <div
      className="mx-auto max-w-lg space-y-5 rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-8 text-center shadow-sm dark:border-emerald-900/50 dark:bg-emerald-950/30"
      data-testid="shift-submitted-lock"
    >
      <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-600 dark:text-emerald-400" />
      <div>
        <div className="font-display text-2xl font-bold text-emerald-900 dark:text-emerald-100">
          Shift submitted
        </div>
        <p className="mt-2 text-sm text-emerald-800 dark:text-emerald-200">
          Your <b>{shiftName}</b> HISAB has been sent to the manager for verification.
          You cannot make any more entries for this shift.
        </p>
        <p className="mt-3 text-xs text-emerald-700/80 dark:text-emerald-300/70">
          Save a copy of the PDF before you leave — you will not see this screen again.
        </p>
      </div>
      <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:justify-center">
        <Button asChild data-testid="download-my-hisab-pdf">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/hisab/${hisabId}/pdf`}
            target="_blank"
            rel="noreferrer"
          >
            Download HISAB PDF
          </a>
        </Button>
        <Button variant="outline" onClick={onDone} data-testid="shift-submitted-done">
          Done — back to home
        </Button>
      </div>
    </div>
  );
}

function ShiftDeadlineBar({ deadline }) {
  const { t: __t } = useTranslation();
  const [, tick] = useState(0);
  useEffect(() => {
    if (!deadline?.deadline_iso) return;
    const t = setInterval(() => tick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, [deadline?.deadline_iso]);
  if (!deadline?.deadline_iso) return null;
  const now = Date.now();
  const end = new Date(deadline.deadline_iso).getTime();
  const minsLeft = Math.round((end - now) / 60_000);
  const isExpired = deadline.is_expired || minsLeft <= 0;
  const isManagerUnlocked = deadline.manager_unlocked;
  // Only surface when close-window matters: expired OR ≤ 60 min remaining
  if (!isExpired && !isManagerUnlocked && minsLeft > 60) return null;
  const timeStr = new Date(deadline.deadline_iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (isExpired && !isManagerUnlocked) {
    return (
      <div
        className="flex items-start gap-2.5 rounded-lg border-2 border-red-300 bg-red-50 p-3 dark:border-red-900/60 dark:bg-red-950/40"
        data-testid="shift-deadline-expired"
      >
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
        <div className="min-w-0 flex-1 text-sm text-red-900 dark:text-red-100">
          <div className="font-bold">{__t("csa.csaportal.shift_submission_window_closed")}</div>
          <div className="mt-0.5 text-[12px] leading-snug">
            The 2-hour close window (until {timeStr}) has passed. You cannot submit until your manager unlocks this shift.
          </div>
        </div>
      </div>
    );
  }
  if (isManagerUnlocked) {
    return (
      <div
        className="flex items-start gap-2.5 rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3 dark:border-emerald-900/60 dark:bg-emerald-950/40"
        data-testid="shift-deadline-unlocked"
      >
        <Unlock className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
        <div className="min-w-0 flex-1 text-sm text-emerald-900 dark:text-emerald-100">
          <div className="font-bold">{__t("csa.csaportal.manager_unlocked_this_shift")}</div>
          <div className="mt-0.5 text-[12px] leading-snug">
            You have an extended window to submit. Please close as soon as possible.
          </div>
        </div>
      </div>
    );
  }
  // Warning: ≤ 60 min left
  const urgent = minsLeft <= 15;
  return (
    <div
      className={"flex items-center gap-2.5 rounded-lg border-2 p-3 " + (urgent
        ? "border-red-300 bg-red-50 dark:border-red-900/60 dark:bg-red-950/40"
        : "border-amber-300 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/40")}
      data-testid="shift-deadline-warning"
    >
      <AlertCircle className={"h-5 w-5 shrink-0 " + (urgent ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400")} />
      <div className={"min-w-0 flex-1 text-sm " + (urgent ? "text-red-900 dark:text-red-100" : "text-amber-900 dark:text-amber-100")}>
        <div className="font-bold">
          {minsLeft <= 0 ? "Close window ending now" : `${minsLeft} min left to submit shift`}
        </div>
        <div className="mt-0.5 text-[12px] leading-snug opacity-90">
          Submit before {timeStr} or ask your manager to unlock.
        </div>
      </div>
    </div>
  );
}

function NozzleGrid({ nozzles, mineIds, onToggle, frozen }) {
  // When frozen, the CSA has committed their allocation for the shift — showing
  // other CSAs' nozzles is visual noise. Filter to just their own.
  const visibleNozzles = frozen ? nozzles.filter((n) => mineIds.has(n.id)) : nozzles;
  // Natural sort — M1, M2, M10 (NOT alphabetic M1, M10, M2). Stable order
  // across renders is critical: CSAs build muscle memory around the sequence.
  const naturalCmp = (a, b) => String(a || "").localeCompare(String(b || ""), undefined, { numeric: true, sensitivity: "base" });
  // Group by machine_code
  const byMachine = {};
  for (const n of visibleNozzles) {
    byMachine[n.machine_code] = byMachine[n.machine_code] || [];
    byMachine[n.machine_code].push(n);
  }
  // Sort each machine's nozzles by code so N1 always renders before N2.
  Object.values(byMachine).forEach((arr) => arr.sort((a, b) => naturalCmp(a.nozzle_code, b.nozzle_code)));
  return (
    <Card className="border-border">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          {frozen ? "Your nozzles" : "Nozzles"}
          {frozen && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
              <Lock className="h-3 w-3" /> Frozen
            </span>
          )}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {frozen
            ? `Only your ${visibleNozzles.length} allocated nozzle${visibleNozzles.length === 1 ? "" : "s"} for this shift. Ask your manager to unfreeze if you need to change.`
            : "Tap a nozzle to allocate it to yourself. Grey = free. Amber = yours. Red = allocated to someone else."}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {Object.keys(byMachine).sort(naturalCmp).map((code) => (
          <div key={code}>
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase text-muted-foreground">
              <span className="rounded bg-secondary px-2 py-0.5">{code}</span>
              <span>{byMachine[code].length} nozzles</span>
            </div>
            <div className="space-y-1.5">
              {byMachine[code].map((n) => {
                const mine = mineIds.has(n.id);
                const busy = n.status === "ALLOCATED" && !mine;
                const disabled = busy || frozen;
                return (
                  <button
                    key={n.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => !disabled && onToggle(n)}
                    data-testid={`nozzle-${n.machine_code}-${n.code}`}
                    className={
                      "flex w-full items-center gap-3 rounded-lg border-2 px-3 py-2.5 text-left transition " +
                      (mine
                        ? "border-amber-500 bg-amber-50 text-amber-950 shadow-sm dark:bg-amber-500/20 dark:text-amber-50 dark:border-amber-400" + (frozen ? " ring-2 ring-emerald-500" : "")
                        : busy
                        ? "cursor-not-allowed border-red-200 bg-red-50/70 text-red-900 opacity-70 dark:bg-red-500/10 dark:text-red-100 dark:border-red-500/40"
                        : frozen
                        ? "cursor-not-allowed border-border bg-background opacity-50"
                        : "border-border bg-card text-foreground hover:border-primary/60 active:scale-[0.99]")
                    }
                  >
                    {/* Left: big checkbox indicator */}
                    <div className={"flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 " + (
                      mine ? "border-amber-600 bg-amber-500 text-white"
                        : busy ? "border-red-400 bg-red-100 dark:bg-red-500/20"
                        : "border-muted-foreground/40 bg-transparent"
                    )}>
                      {mine ? (frozen ? <Lock className="h-3.5 w-3.5" /> : <CircleCheck className="h-4 w-4" />)
                       : busy ? <span className="text-[9px] font-bold text-red-600">✕</span>
                       : null}
                    </div>
                    {/* Middle: name */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-bold">{n.code}</span>
                        <span className="text-[11px] font-semibold text-foreground/80">{n.product_name}</span>
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        Rs. {n.rate?.toFixed(2)}/L
                        {busy && n.allocation?.user_name && <span className="ml-2 text-red-600">· Taken by {n.allocation.user_name}</span>}
                      </div>
                    </div>
                    {/* Right: state badge */}
                    <div className="shrink-0 text-[10px] font-bold uppercase tracking-wider">
                      {mine ? (frozen ? "LOCKED" : "MINE") : busy ? "TAKEN" : "TAP TO PICK"}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {visibleNozzles.length === 0 && (
          <div className="p-6 text-center text-sm text-muted-foreground">
            {frozen ? "You have no nozzles allocated for this shift." : "No nozzles configured yet"}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// -------- Tabs --------
function ReadingsTab({ allocs, onReload }) {
  const { t: __t } = useTranslation();
  const [drafts, setDrafts] = useState({});
  const [handoverAlloc, setHandoverAlloc] = useState(null);
  const [cashHandoverOpen, setCashHandoverOpen] = useState(false);
  const [continuityWarnings, setContinuityWarnings] = useState({}); // { [allocId]: warning }
  const [savingIds, setSavingIds] = useState({}); // { [allocId]: true } — in-flight guard
  const [justSavedIds, setJustSavedIds] = useState({}); // { [allocId]: timestamp } — brief "Saved ✓" flash
  const draftsRef = useRef(drafts);
  const allocsRef = useRef(allocs);
  useEffect(() => { draftsRef.current = drafts; }, [drafts]);
  useEffect(() => { allocsRef.current = allocs; }, [allocs]);

  // Init from server ONLY for allocations we haven't touched yet; preserve in-flight edits.
  useEffect(() => {
    setDrafts((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const a of allocs) {
        if (!next[a.id]) {
          next[a.id] = {
            opening: a.opening ?? "", closing: a.closing ?? "", testing: a.testing ?? "0",
          };
          changed = true;
        }
      }
      // Drop drafts whose alloc has been released
      const validIds = new Set(allocs.map((a) => a.id));
      for (const k of Object.keys(next)) {
        if (!validIds.has(k)) { delete next[k]; changed = true; }
      }
      return changed ? next : prev;
    });
  }, [allocs]);

  if (allocs.length === 0) return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{__t("csa.csaportal.allocate_some_nozzles_above_first")}</p>
      <CashHandoverDialog open={cashHandoverOpen} onClose={() => setCashHandoverOpen(false)} onSaved={onReload} />
    </div>
  );

  const clampNum = (v) => {
    const s = String(v ?? "").replace(/[^0-9.]/g, "");
    const parts = s.split(".");
    return parts.length > 1 ? parts[0] + "." + parts.slice(1).join("").replace(/\./g, "") : s;
  };

  const setField = (id, key, raw) => {
    const v = clampNum(raw);
    setDrafts((s) => ({ ...s, [id]: { ...s[id], [key]: v } }));
  };

  // Dirty-check: local draft value differs from server-saved value on that alloc
  const isDirty = (a, d) => {
    const cmp = (local, server) => {
      const l = local === "" || local == null ? null : Number(local);
      const s = server == null ? null : Number(server);
      return l !== s;
    };
    return cmp(d.opening, a.opening) || cmp(d.closing, a.closing) || cmp(d.testing, a.testing);
  };

  const flashSaved = (id) => {
    setJustSavedIds((prev) => ({ ...prev, [id]: Date.now() }));
    setTimeout(() => {
      setJustSavedIds((prev) => {
        const nxt = { ...prev };
        delete nxt[id];
        return nxt;
      });
    }, 1600);
  };

  const save = async (id, { silent = false } = {}) => {
    // In-flight guard — never fire a duplicate save for the same allocation.
    if (savingIds[id]) return false;
    const d = draftsRef.current[id] || {};
    const openN = d.opening !== "" ? Number(d.opening) : null;
    const closeN = d.closing !== "" ? Number(d.closing) : null;
    const testN = d.testing !== "" ? Number(d.testing) : null;
    if (openN != null && closeN != null && closeN < openN) {
      if (!silent) toast.error(`Closing (${closeN}) cannot be less than opening (${openN})`);
      return false;
    }
    const payload = {
      nozzle_allocation_id: id,
      opening: openN, closing: closeN, testing: testN,
    };

    // Offline path — queue the save and optimistically show success.
    if (!isOnline()) {
      enqueue({
        url: "/hisab/draft/reading",
        method: "PATCH",
        body: payload,
        meta: { kind: "reading", allocation_id: id },
      });
      if (!silent) toast.success(__t("csa.csaportal.saved_offline_will_sync_when_network_ret"));
      flashSaved(id);
      return true;
    }

    setSavingIds((prev) => ({ ...prev, [id]: true }));
    try {
      const { data } = await api.patch("/hisab/draft/reading", payload);
      // Continuity warning — persistent inline note near the nozzle tile
      const w = data?.warning;
      setContinuityWarnings((prev) => {
        const next = { ...prev };
        if (w && w.kind === "continuity_mismatch") next[id] = w;
        else delete next[id];
        return next;
      });
      // OPTIMISTIC LOCAL PATCH: mutate the alloc in place so the UI immediately
      // shows "Saved" without a full summary refetch (which is what caused the lag).
      const a = allocsRef.current.find((x) => x.id === id);
      if (a) {
        a.opening = openN;
        a.closing = closeN;
        a.testing = testN;
      }
      flashSaved(id);
      if (!silent) {
        if (w?.kind === "continuity_mismatch") toast.warning(w.message);
      }
      // Fire-and-forget refetch — needed to update summary totals in other tabs,
      // but NOT awaited so the Save button feels instant.
      Promise.resolve(onReload()).catch(() => {});
      return true;
    } catch (e) {
      if (!silent) toast.error(formatApiError(e));
      return false;
    } finally {
      setSavingIds((prev) => {
        const nxt = { ...prev };
        delete nxt[id];
        return nxt;
      });
    }
  };

  const autoSave = async (id) => {
    const d = draftsRef.current[id];
    const a = allocsRef.current.find((x) => x.id === id);
    if (!a || !d) return;
    if (!isDirty(a, d)) return;
    if (savingIds[id]) return;
    await save(id, { silent: true });
  };

  return (
    <div className="space-y-3">
      {[...allocs].sort((a, b) => {
        // Natural sort by machine then nozzle — daily order stays constant.
        const cmp = String(a.machine_code || "").localeCompare(String(b.machine_code || ""), undefined, { numeric: true, sensitivity: "base" });
        if (cmp !== 0) return cmp;
        return String(a.nozzle_code || "").localeCompare(String(b.nozzle_code || ""), undefined, { numeric: true, sensitivity: "base" });
      }).map((a) => {
        const d = drafts[a.id] || {};
        const litres = Math.max(0, Number(d.closing || 0) - Number(d.opening || 0) - Number(d.testing || 0));
        const amt = litres * (a.rate || 0);
        const dirty = isDirty(a, d);
        const saving = !!savingIds[a.id];
        const justSaved = !!justSavedIds[a.id];
        const savedOpening = a.opening != null;
        const savedClosing = a.closing != null;
        return (
          <div
            key={a.id}
            className={"overflow-hidden rounded-xl border-2 transition-colors " + (dirty ? "border-amber-400 bg-amber-50/60 dark:bg-amber-500/5" : "border-border bg-card")}
            data-testid={`reading-alloc-${a.machine_code}-${a.nozzle_code}`}
          >
            {/* Header — nozzle identity + status */}
            <div className="flex items-center justify-between gap-2 border-b border-border/50 bg-background/50 px-3 py-2.5">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="rounded-md bg-primary/10 px-2 py-0.5 font-mono text-xs font-bold text-primary">
                    {a.machine_code}·{a.nozzle_code}
                  </span>
                  <span className="truncate text-sm font-semibold text-foreground">{a.product_name}</span>
                </div>
                <div className="mt-0.5 text-[10px] text-muted-foreground">{__t("csa.rate")}<b className="tabular-nums text-foreground">₹{a.rate?.toFixed(2)}/L</b>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {saving && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-sky-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white" data-testid={`saving-${a.machine_code}-${a.nozzle_code}`}>
                    <Loader2 className="h-3 w-3 animate-spin" /> Saving
                  </span>
                )}
                {!saving && dirty && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white" data-testid={`unsaved-${a.machine_code}-${a.nozzle_code}`}>
                    <AlertCircle className="h-3 w-3" /> Unsaved
                  </span>
                )}
                {!saving && !dirty && justSaved && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white transition-opacity" data-testid={`justsaved-${a.machine_code}-${a.nozzle_code}`}>
                    <CircleCheck className="h-3 w-3" /> Saved ✓
                  </span>
                )}
                {!saving && !dirty && !justSaved && (savedOpening || savedClosing) && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200" data-testid={`saved-${a.machine_code}-${a.nozzle_code}`}>
                    <CircleCheck className="h-3 w-3" /> Saved
                  </span>
                )}
              </div>
            </div>

            {/* Inputs — stacked full-width on mobile, side-by-side on tablet */}
            <div className="p-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {a.opening_locked ? (
                  <div className="flex flex-col gap-1 rounded-lg border-2 border-emerald-400 bg-emerald-50 px-3 py-2.5 dark:border-emerald-500/40 dark:bg-emerald-500/10" data-testid={`open-locked-${a.machine_code}-${a.nozzle_code}`}>
                    <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-800 dark:text-emerald-200">
                      <Lock className="h-3 w-3" /> Opening (manager-locked)
                    </div>
                    <div className="font-mono text-2xl font-bold tabular-nums tracking-tight text-emerald-900 dark:text-emerald-100">
                      {Number(a.opening).toFixed(2)}
                    </div>
                    <div className="text-[10px] text-emerald-700 dark:text-emerald-300">
                      set by manager override
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-1">
                    <FieldNum
                      label={savedOpening ? "Opening ✓" : "Opening reading"}
                      value={d.opening}
                      onChange={(v) => setField(a.id, "opening", v)}
                      onBlur={() => autoSave(a.id)}
                      testId={`open-${a.machine_code}-${a.nozzle_code}`}
                    />
                    {a.previous_close_suggestion != null && !d.opening && (
                      <button
                        type="button"
                        className="self-start text-[10px] font-semibold text-sky-700 underline decoration-dotted dark:text-sky-300"
                        onClick={() => {
                          setField(a.id, "opening", String(a.previous_close_suggestion));
                          autoSave(a.id);
                        }}
                        data-testid={`use-prev-close-${a.machine_code}-${a.nozzle_code}`}
                      >
                        Prev close was {Number(a.previous_close_suggestion).toFixed(2)} · use it
                      </button>
                    )}
                  </div>
                )}
                <FieldNum
                  label={savedClosing ? "Closing ✓" : "Closing reading"}
                  value={d.closing}
                  onChange={(v) => setField(a.id, "closing", v)}
                  onBlur={() => autoSave(a.id)}
                  testId={`close-${a.machine_code}-${a.nozzle_code}`}
                />
              </div>

              {/* Testing — small, compact, since 99% of the time it's 0 */}
              <div className="mt-3 grid grid-cols-[1fr_auto] items-end gap-3">
                <FieldNum
                  label={__t("csa.csaportal.testing_rs_0_loss")}
                  size="sm"
                  value={d.testing}
                  onChange={(v) => setField(a.id, "testing", v)}
                  onBlur={() => autoSave(a.id)}
                  testId={`test-${a.machine_code}-${a.nozzle_code}`}
                />
                {a.closing == null && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setHandoverAlloc(a)}
                    data-testid={`handover-${a.machine_code}-${a.nozzle_code}`}
                    className="h-11 whitespace-nowrap px-3 text-xs"
                  >
                    <ArrowRightLeft className="mr-1 h-3.5 w-3.5" /> Hand over
                  </Button>
                )}
              </div>
            </div>

            {/* Continuity warning — big, high-contrast */}
            {continuityWarnings[a.id] && (
              <div className="mx-3 flex items-start gap-2 rounded-lg border-2 border-amber-400 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-500/50 dark:bg-amber-500/10 dark:text-amber-100" data-testid={`continuity-warn-${a.machine_code}-${a.nozzle_code}`}>
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <div className="text-sm font-bold">{__t("csa.csaportal.reading_mismatch_with_previous_shift")}</div>
                  <div className="mt-0.5">{continuityWarnings[a.id].message}</div>
                  <div className="mt-1 text-[10px] opacity-80">
                    Prev close by {continuityWarnings[a.id].previous_csa || "another CSA"} on {continuityWarnings[a.id].previous_date}
                  </div>
                </div>
              </div>
            )}

            {/* Live-computed total — BIG at-a-glance verification band */}
            <div className={"flex items-center justify-between gap-3 px-3 py-3 " + (litres > 0 ? "bg-emerald-50/60 dark:bg-emerald-500/5" : "bg-secondary/40")}>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  This shift dispensed
                </div>
                <div className="flex items-baseline gap-2">
                  <div className="font-mono text-2xl font-bold tabular-nums tracking-tight text-foreground">
                    {litres.toFixed(2)}
                    <span className="ml-0.5 text-sm font-semibold text-muted-foreground">L</span>
                  </div>
                  <div className="font-mono text-xl font-bold tabular-nums tracking-tight text-emerald-700 dark:text-emerald-400">
                    {rupee(amt)}
                  </div>
                </div>
              </div>
              <Button
                size="lg"
                onClick={() => save(a.id)}
                disabled={!dirty || saving}
                data-testid={`save-${a.machine_code}-${a.nozzle_code}`}
                className={"h-12 min-w-[110px] px-6 text-base font-bold " + (dirty && !saving ? "bg-primary shadow-md shadow-primary/25" : "")}
              >
                {saving ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Saving</>
                  : dirty ? "Save"
                  : <><CircleCheck className="mr-1 h-4 w-4" /> Saved</>}
              </Button>
            </div>
          </div>
        );
      })}
      {handoverAlloc && (
        <HandoverDialog
          allocation={handoverAlloc}
          open={!!handoverAlloc}
          onClose={() => setHandoverAlloc(null)}
          onDone={onReload}
        />
      )}
      <CashHandoverDialog open={cashHandoverOpen} onClose={() => setCashHandoverOpen(false)} onSaved={onReload} />
    </div>
  );
}


/** Compact strip retained for legacy callers — currently unused. */
function CashHandoverActionRow() { return null; }


/**
 * Dedicated CSA "Cash" tab — mid-shift cash handover to the manager.
 * Placed in the regular shift flow (tabs) instead of a floating strip.
 * Shows history with statuses (pending / approved / auto-approved / rejected).
 * PENDING rows can be edited or voided until the manager acts.
 */
function CashHandoverTab({ onReload }) {
  const { t: __t } = useTranslation();
  const [history, setHistory] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({ amount: "", note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/csa/cash-handover/mine");
      setHistory(data || { items: [], total: 0 });
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (row) => {
    setEditRow(row);
    setEditForm({ amount: String(row.amount || ""), note: row.note || "" });
  };
  const saveEdit = async () => {
    if (!editRow) return;
    const amt = Number(editForm.amount);
    if (!amt || amt <= 0) return toast.error(__t("csa.csaportal.enter_a_valid_amount"));
    setSaving(true);
    try {
      await api.patch(`/csa/cash-handover/${editRow.id}`, { amount: amt, note: editForm.note || null });
      toast.success(__t("csa.csaportal.handover_updated"));
      setEditRow(null); load(); onReload && onReload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const voidPending = async (id) => {
    if (!window.confirm("Void this pending handover? Manager will not see it anymore.")) return;
    try {
      await api.delete(`/csa/cash-handover/${id}`);
      toast.success(__t("csa.csaportal.voided"));
      load(); onReload && onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rows = history.items || [];
  const pendingCt = rows.filter((r) => (r.status || "approved") === "pending").length;
  const approvedCt = rows.filter((r) => ["approved", "auto_approved_on_close"].includes(r.status || "approved")).length;

  return (
    <div className="space-y-3" data-testid="cash-handover-tab">
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 dark:border-emerald-900/50 dark:bg-emerald-950/30">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-sm font-bold text-emerald-900 dark:text-emerald-100">
              <HandCoins className="h-4 w-4" /> Cash handover to manager
            </div>
            <p className="mt-0.5 text-[11px] text-emerald-800 dark:text-emerald-300/90">
              Hand over cash mid-shift — <b>manager confirms</b> in cashbook. Final handover
              also happens automatically when you close the shift.
            </p>
          </div>
          <Button size="sm" onClick={() => setDialogOpen(true)} data-testid="cash-handover-open">
            <Plus className="h-3.5 w-3.5" />{__t("common.submit")}</Button>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <div className="rounded-md bg-background/60 p-2 text-center">
            <div className="text-[10px] uppercase text-muted-foreground">{__t("csa.csaportal.total_this_shift")}</div>
            <div className="font-mono text-sm font-bold text-foreground">Rs. {Number(history.total || 0).toLocaleString("en-IN")}</div>
          </div>
          <div className="rounded-md bg-background/60 p-2 text-center">
            <div className="text-[10px] uppercase text-muted-foreground">{__t("csa.csaportal.approved")}</div>
            <div className="font-mono text-sm font-bold text-emerald-700">{approvedCt}</div>
          </div>
          <div className="rounded-md bg-background/60 p-2 text-center">
            <div className="text-[10px] uppercase text-muted-foreground">{__t("csa.csaportal.pending")}</div>
            <div className="font-mono text-sm font-bold text-amber-700">{pendingCt}</div>
          </div>
        </div>
      </div>

      <div className="space-y-2" data-testid="cash-handover-history">
        {rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
            No handovers yet. Tap <b>{__t("common.submit")}</b> when you hand over cash to your manager.
          </div>
        ) : (
          rows.map((r) => {
            const status = r.status || "approved";
            const isPending = status === "pending";
            const chipTone = isPending ? "bg-amber-100 text-amber-900" :
              status === "rejected" ? "bg-rose-100 text-rose-800" :
              status === "auto_approved_on_close" ? "bg-sky-100 text-sky-800" :
              "bg-emerald-100 text-emerald-800";
            const chipLabel = isPending ? "Awaiting manager" :
              status === "rejected" ? "Rejected" :
              status === "auto_approved_on_close" ? "Auto-approved (shift close)" :
              `Confirmed${r.manager_name ? ` by ${r.manager_name}` : ""}`;
            return (
              <div key={r.id} className="flex items-start justify-between gap-2 rounded-lg border border-border bg-card p-3" data-testid={`csa-handover-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-base font-bold text-foreground">Rs. {Number(r.amount).toLocaleString("en-IN")}</div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">
                    <Clock className="mr-0.5 inline h-3 w-3" />
                    {new Date(r.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                  </div>
                  {r.note && <div className="mt-0.5 text-[11px] italic text-muted-foreground">&ldquo;{r.note}&rdquo;</div>}
                  {r.rejection_reason && (
                    <div className="mt-0.5 text-[11px] text-rose-700">Rejected: {r.rejection_reason}</div>
                  )}
                  <div className="mt-1">
                    <span className={"inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold " + chipTone}>
                      {chipLabel}
                    </span>
                  </div>
                </div>
                {isPending && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`csa-handover-edit-${r.id}`} title={__t("csa.csaportal.edit")}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => voidPending(r.id)} data-testid={`csa-handover-void-${r.id}`} title={__t("csa.csaportal.void")}>
                      <XCircle className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <CashHandoverDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={() => { load(); onReload && onReload(); }} />

      <Dialog open={!!editRow} onOpenChange={(v) => !v && setEditRow(null)}>
        <DialogContent data-testid="cash-handover-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-4 w-4" /> Edit pending handover
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{__t("csa.csaportal.amount_rs")}<span className="text-red-600">*</span></Label>
              <Input inputMode="decimal" value={editForm.amount}
                onChange={(e) => setEditForm({ ...editForm, amount: e.target.value.replace(/[^0-9.]/g, "") })}
                className="font-mono" data-testid="csa-handover-edit-amount" />
            </div>
            <div>
              <Label>{__t("csa.csaportal.note_optional")}</Label>
              <Textarea rows={2} value={editForm.note}
                onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                data-testid="csa-handover-edit-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRow(null)} disabled={saving}>{__t("common.cancel")}</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="csa-handover-edit-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FieldNum({ label, value, onChange, onBlur, testId, size = "lg", hint }) {
  const cls = size === "sm"
    ? "h-11 text-base font-mono tabular-nums font-semibold"
    : "h-14 text-2xl font-mono tabular-nums font-bold tracking-tight";
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </Label>
      <Input
        inputMode="decimal"
        type="text"
        pattern="[0-9]*\.?[0-9]*"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        data-testid={testId}
        className={cls + " border-2 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30"}
        placeholder="0"
      />
      {hint && (
        <div className="text-[10px] text-muted-foreground">{hint}</div>
      )}
    </div>
  );
}

function OnlineTab({ draft, onReload, pump }) {
  const { t: __t } = useTranslation();
  const [amount, setAmount] = useState("");
  const [ref, setRef] = useState("");
  const [methods, setMethods] = useState([]); // [{code,label}]
  const [method, setMethod] = useState("UPI");
  const amountRef = useRef(null);
  const location = useLocation();

  // Auto-focus amount field if deep-linked with ?quick=1 (from App Shortcut)
  useEffect(() => {
    if (new URLSearchParams(location.search).get("quick") === "1") {
      setTimeout(() => amountRef.current?.focus(), 250);
    }
  }, [location.search]);

  // Merge pump's enabled methods with the master reference labels
  useEffect(() => {
    api.get("/reference/online-methods").then((r) => {
      const master = r.data.methods || [];
      const enabled = pump?.online_methods || ["UPI", "CARD"];
      const merged = master.filter((m) => enabled.includes(m.code));
      setMethods(merged.length ? merged : master);
      // Only reset method if the current selection is not in the enabled set.
      setMethod((prev) => (merged.some((m) => m.code === prev) ? prev : (merged[0]?.code || "UPI")));
    }).catch(() => setMethods([{ code: "UPI", label: "UPI" }, { code: "CARD", label: "Card" }]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pump?.id]);

  const add = async () => {
    if (!amount || Number(amount) <= 0) return toast.error(__t("csa.csaportal.enter_a_valid_amount"));
    try {
      await api.post("/hisab/draft/online", { amount: Number(amount), method, reference: ref || null });
      setAmount(""); setRef("");
      toast.success(__t("csa.csaportal.online_payment_logged"));
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3">
      <div className="grid gap-2 md:grid-cols-4">
        <Input
          ref={amountRef}
          inputMode="decimal" type="text" pattern="[0-9]*\.?[0-9]*"
          placeholder={__t("csa.amount")}
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
          data-testid="online-amount"
          className="h-11 md:h-9"
        />
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger data-testid="online-method" className="h-11 md:h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            {methods.map((m) => (
              <SelectItem key={m.code} value={m.code}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input placeholder={__t("csa.csaportal.reference_txn_id")} value={ref} onChange={(e) => setRef(e.target.value)} data-testid="online-ref" className="h-11 md:h-9" />
        <Button onClick={add} disabled={!amount} data-testid="online-add" className="h-11 md:h-9"><Plus className="h-4 w-4" /> Add</Button>
      </div>
      <p className="text-[10px] text-muted-foreground">
        Methods shown here are the ones your dealer has enabled for this pump. Ask them to add more in Setup → Payment methods.
      </p>
      <div className="rounded-md border border-border bg-card p-3">
        <div className="mb-1 flex items-center justify-between text-xs font-semibold uppercase text-muted-foreground">
          <span>{__t("csa.csaportal.saved_online_payments_today")}</span>
          <span className="tabular-nums">{(draft?.online_txs || []).length} entries · <b>{rupee((draft?.online_txs || []).reduce((s, t) => s + Number(t.amount || 0), 0))}</b></span>
        </div>
        <List
          items={draft?.online_txs || []}
          labelFn={(t) => {
            const label = methods.find((m) => m.code === t.method)?.label || t.method || "UPI";
            return `${label}${t.reference ? " · " + t.reference : ""}`;
          }}
          amountFn={(t) => t.amount}
          testPrefix="online-row"
          onDelete={async (t) => {
            await api.delete(`/hisab/draft/online/${t.id}`);
            toast.success(__t("csa.csaportal.online_entry_deleted"));
            onReload();
          }}
        />
      </div>
    </div>
  );
}

function CreditTab({ draft, onReload, pump }) {
  const { t: __t } = useTranslation();
  const requirePhotos = !!pump?.require_credit_photos;
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null); // credit customer object
  const [amount, setAmount] = useState("");
  const [vehicleOverride, setVehicleOverride] = useState("");
  const [note, setNote] = useState("");
  const [slip, setSlip] = useState(null);
  const [bill, setBill] = useState(null);
  const [saving, setSaving] = useState(false);
  // Multi-product line items — optional detail. When empty, we fall back to a single `amount`.
  const [lineItems, setLineItems] = useState([]); // [{product_name, litres, rate, amount}]
  const [products, setProducts] = useState([]);

  // Khatabook-style confirm + undo
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastPosted, setLastPosted] = useState(null);   // { id, amount, customer_name } | null
  const [creditConfig, setCreditConfig] = useState({ default_due_days: 15, undo_window_sec: 60, require_amount_reentry: true });

  useEffect(() => {
    api.get("/reference").then((r) => setProducts(r.data.products || [])).catch(() => {});
    api.get("/credit-config").then((r) => setCreditConfig(r.data || {})).catch(() => {});
  }, []);

  const readFile = (file, setter) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = () => setter(r.result);
    r.readAsDataURL(file);
  };

  // Debounced search — hits dealer-managed credit customers only
  useEffect(() => {
    if (selected) return; // don't search while a customer is picked
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (search.trim()) params.set("q", search.trim());
        const { data } = await api.get(`/credit-customers/dropdown/search${params.toString() ? "?" + params : ""}`);
        setResults(data.results || []);
      } catch { /* ignore */ }
    }, 200);
    return () => clearTimeout(t);
  }, [search, selected]);

  const reset = () => {
    setSelected(null); setSearch(""); setAmount(""); setVehicleOverride("");
    setNote(""); setSlip(null); setBill(null); setLineItems([]);
  };

  const addLine = () => {
    const firstP = products[0];
    setLineItems([...lineItems, { product_id: firstP?.id || "", product_name: firstP?.name || "", rate: firstP?.rate || 0, litres: "", amount: "" }]);
  };
  const updateLine = (idx, patch) => {
    setLineItems((ls) => ls.map((li, i) => (i === idx ? { ...li, ...patch } : li)));
  };
  const removeLine = (idx) => setLineItems((ls) => ls.filter((_, i) => i !== idx));

  // Auto-sync amount from line items if present
  const lineTotal = lineItems.reduce((s, li) => s + Number(li.amount || 0), 0);
  useEffect(() => {
    if (lineItems.length > 0) setAmount(String(lineTotal.toFixed(2)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineTotal, lineItems.length]);

  const submit = async () => {
    if (!selected) return toast.error(__t("csa.csaportal.pick_a_credit_customer_from_the_list_fir"));
    if (!amount || Number(amount) <= 0) return toast.error(__t("csa.csaportal.enter_a_valid_amount"));
    if (requirePhotos && (!slip || !bill)) {
      return toast.error(__t("csa.csaportal.this_pump_requires_both_credit_slip_and"));
    }
    // If line items were used, validate each
    if (lineItems.length > 0) {
      for (const li of lineItems) {
        if (!li.product_name) return toast.error(__t("csa.csaportal.every_product_line_needs_a_product_selec"));
        if (!li.amount || Number(li.amount) <= 0) return toast.error(__t("csa.csaportal.every_product_line_needs_a_positive_amou"));
      }
    }
    // Open double-check confirm dialog (Khatabook parity)
    if (creditConfig.require_amount_reentry !== false) {
      setConfirmOpen(true);
      return;
    }
    // Fallback: pump admin disabled the double-check
    await commitSave(Number(amount));
  };

  const commitSave = async (confirmedAmount) => {
    setSaving(true);
    try {
      const { data: created } = await api.post("/hisab/draft/credit", {
        credit_customer_id: selected.id,
        amount: Number(amount),
        confirmed_amount: Number(confirmedAmount),
        vehicle: vehicleOverride || selected.vehicle || null,
        note: note || null,
        slip_photo_base64: slip, bill_photo_base64: bill,
        line_items: lineItems.length > 0 ? lineItems.map((li) => ({
          product_id: li.product_id || null,
          product_name: li.product_name,
          litres: li.litres ? Number(li.litres) : null,
          rate: li.rate ? Number(li.rate) : null,
          amount: Number(li.amount),
        })) : null,
      });
      setLastPosted({
        id: created?.id,
        amount: created?.amount || Number(amount),
        customer_name: selected.name,
      });
      toast.success(__t("csa.csaportal.credit_entry_saved"));
      setConfirmOpen(false);
      reset();
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      {!selected ? (
        <>
          <div>
            <Label>{__t("csa.csaportal.search_credit_customer")}</Label>
            <div className="relative">
              <UserIcon className="pointer-events-none absolute left-2 top-3 h-4 w-4 text-muted-foreground/70" />
              <Input
                placeholder="Type name, phone or vehicle…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="credit-search"
                className="pl-8 h-11 md:h-9"
              />
            </div>
            {results.length === 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                No matches. Ask your dealer to add the customer under <b>{__t("csa.csaportal.credit_customers")}</b>.
              </p>
            )}
          </div>
          {results.length > 0 && (
            <div className="max-h-64 overflow-auto rounded-lg border border-border bg-card divide-y">
              {results.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setSelected(r)}
                  className="flex w-full items-center justify-between p-3 text-left hover:bg-background"
                  data-testid={`credit-pick-${r.id}`}
                >
                  <div className="min-w-0">
                    <div className="truncate font-semibold">{r.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.phone || "no phone"}{r.vehicle ? " · " + r.vehicle : ""}
                    </div>
                  </div>
                  <div className="text-right text-xs">
                    {/* Balance hidden from CSA — dealer-only info */}
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{__t("csa.csaportal.credit_customer")}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex items-start justify-between rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="credit-selected">
            <div className="min-w-0">
              <div className="text-xs uppercase text-amber-700">{__t("csa.csaportal.selected_credit_customer")}</div>
              <div className="truncate text-base font-semibold text-amber-900">{selected.name}</div>
              <div className="text-xs text-amber-800">
                {selected.phone || "no phone"}{selected.vehicle ? " · " + selected.vehicle : ""}
                {/* outstanding + credit_limit intentionally hidden from CSA — dealer-only info */}
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={reset} data-testid="credit-reset">{__t("csa.csaportal.change")}</Button>
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            <div>
              <Label>Amount (Rs.) *</Label>
              <Input inputMode="decimal" type="text" pattern="[0-9]*\.?[0-9]*"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                data-testid="credit-amount" className="h-11 md:h-9"
                readOnly={lineItems.length > 0}
                title={lineItems.length > 0 ? "Amount is auto-computed from product lines below" : ""}
              />
              {lineItems.length > 0 && (
                <p className="mt-0.5 text-[10px] text-muted-foreground">Auto-summed from {lineItems.length} product line{lineItems.length > 1 ? "s" : ""}</p>
              )}
            </div>
            <div>
              <Label>{__t("csa.csaportal.vehicle_override")}</Label>
              <Input value={vehicleOverride} onChange={(e) => setVehicleOverride(e.target.value.toUpperCase())} data-testid="credit-vehicle" className="h-11 md:h-9" />
            </div>
          </div>

          {/* Optional per-product breakdown — for multi-fuel slips */}
          <div className="rounded-md border border-dashed border-border p-2.5">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{__t("csa.csaportal.products_on_this_slip_optional")}</div>
              <Button size="sm" variant="outline" onClick={addLine} data-testid="credit-add-line" disabled={products.length === 0}>
                <Plus className="h-3 w-3" /> Add product
              </Button>
            </div>
            {products.length === 0 && lineItems.length === 0 && (
              <p className="mt-1 text-[10px] text-muted-foreground">{__t("csa.csaportal.no_products_configured_skip_this_section")}</p>
            )}
            {lineItems.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {lineItems.map((li, idx) => (
                  <div key={idx} className="grid grid-cols-12 items-center gap-1.5 rounded border border-border/60 bg-background/60 p-1.5" data-testid={`credit-line-${idx}`}>
                    <div className="col-span-4">
                      <Select
                        value={li.product_id || li.product_name}
                        onValueChange={(val) => {
                          const p = products.find((pp) => pp.id === val);
                          updateLine(idx, {
                            product_id: p?.id || val,
                            product_name: p?.name || val,
                            rate: p?.rate || li.rate,
                          });
                        }}
                      >
                        <SelectTrigger data-testid={`credit-line-product-${idx}`} className="h-9"><SelectValue placeholder={__t("csa.product")} /></SelectTrigger>
                        <SelectContent>
                          {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      <Input
                        inputMode="decimal" placeholder={__t("csa.litres")}
                        value={li.litres}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^0-9.]/g, "");
                          const litres = Number(v || 0);
                          const rate = Number(li.rate || 0);
                          const amt = litres && rate ? (litres * rate).toFixed(2) : li.amount;
                          updateLine(idx, { litres: v, amount: amt });
                        }}
                        className="h-9"
                        data-testid={`credit-line-litres-${idx}`}
                      />
                    </div>
                    <div className="col-span-3">
                      <Input
                        inputMode="decimal" placeholder={__t("csa.amount")}
                        value={li.amount}
                        onChange={(e) => updateLine(idx, { amount: e.target.value.replace(/[^0-9.]/g, "") })}
                        className="h-9"
                        data-testid={`credit-line-amount-${idx}`}
                      />
                    </div>
                    <div className="col-span-2 text-right">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500 hover:bg-red-50" onClick={() => removeLine(idx)} data-testid={`credit-line-remove-${idx}`}>×</Button>
                    </div>
                  </div>
                ))}
                <div className="pt-1 text-right text-[11px] font-semibold text-foreground">
                  Line total: {rupee(lineTotal)}
                </div>
              </div>
            )}
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            <FileField
              label={`Machine slip photo${requirePhotos ? " *" : " (optional)"}`}
              value={slip}
              onChange={(f) => f && readFile(f, setSlip)}
              testId="credit-slip"
            />
            <FileField
              label={`Bill photo${requirePhotos ? " *" : " (optional)"}`}
              value={bill}
              onChange={(f) => f && readFile(f, setBill)}
              testId="credit-bill"
            />
          </div>
          {requirePhotos && (!slip || !bill) && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100" data-testid="credit-photo-requirement">
              This pump requires <b>both</b> the credit slip photo and the bill photo for every credit entry.
            </div>
          )}
          <Textarea placeholder={__t("csa.csaportal.note_optional")} value={note} onChange={(e) => setNote(e.target.value)} data-testid="credit-note" />
          <Button onClick={submit} disabled={saving || !amount} data-testid="credit-add" className="h-11 md:h-9">
            <Plus className="h-4 w-4" /> {saving ? "Saving…" : "Save credit entry"}
          </Button>
        </>
      )}
      <List
        items={draft?.credit_entries || []}
        labelFn={(c) => `${c.customer_name}${c.vehicle ? " · " + c.vehicle : ""}`}
        amountFn={(c) => c.amount}
        testPrefix="credit-row"
        onDelete={async (c) => {
          await api.delete(`/hisab/draft/credit/${c.id}`);
          toast.success(__t("csa.csaportal.credit_entry_deleted"));
          onReload();
        }}
      />
      {/* Double-check + undo (Khatabook-style) */}
      <AmountConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        amount={Number(amount || 0)}
        customerName={selected?.name || ""}
        vehicle={vehicleOverride || selected?.vehicle || null}
        dueDate={null}
        loading={saving}
        onConfirm={(confirmed) => commitSave(confirmed)}
      />
      <UndoToast
        entry={lastPosted}
        windowSec={creditConfig.undo_window_sec || 60}
        onUndone={() => { setLastPosted(null); onReload(); }}
        onDismiss={() => setLastPosted(null)}
      />
    </div>
  );
}

function FileField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-xs uppercase text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-2">
        <input type="file" accept="image/*" capture="environment" onChange={(e) => onChange(e.target.files?.[0])} data-testid={testId} className="text-xs" />
        {value && <img src={value} alt="preview" className="h-10 w-10 rounded object-cover" />}
      </div>
    </div>
  );
}

// -------- Lube tab --------
function LubeTab({ draft, onReload }) {
  const { t: __t } = useTranslation();
  const [lubeItems, setLubeItems] = useState([]);
  const [creditCustomers, setCreditCustomers] = useState([]);
  const [form, setForm] = useState({
    lube_item_id: "",
    item_name: "",
    qty: "",
    unit_price: "",
    payment_mode: "CASH",
    credit_customer_id: "",
    vehicle: "",
    note: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/hisab/draft/lube-items").then((r) => setLubeItems(r.data?.items || []))
      .catch(() => setLubeItems([]));
    api.get("/credit-customers").then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.credit_customers || []);
      setCreditCustomers(list);
    }).catch(() => setCreditCustomers([]));
  }, []);

  const entries = draft?.lube_sales || [];
  const total = entries.reduce((s, e) => s + Number(e.amount || 0), 0);

  const onPickItem = (id) => {
    const item = lubeItems.find((i) => i.id === id);
    setForm((f) => ({
      ...f,
      lube_item_id: id,
      item_name: item?.name || "",
      unit_price: item?.unit_price != null ? String(item.unit_price) : f.unit_price,
    }));
  };

  const submit = async () => {
    if (!form.item_name && !form.lube_item_id) return toast.error(__t("csa.csaportal.pick_a_lube_product_or_enter_a_name"));
    const qty = Number(form.qty);
    const price = Number(form.unit_price);
    if (!qty || qty <= 0) return toast.error(__t("csa.csaportal.enter_quantity"));
    if (isNaN(price) || price < 0) return toast.error(__t("csa.csaportal.enter_unit_price"));
    if (form.payment_mode === "CREDIT" && !form.credit_customer_id) {
      return toast.error(__t("csa.csaportal.pick_a_credit_customer_for_credit_sale"));
    }
    setSaving(true);
    try {
      await api.post("/hisab/draft/lube-sale", {
        lube_item_id: form.lube_item_id || null,
        item_name: form.item_name || null,
        qty, unit_price: price,
        payment_mode: form.payment_mode,
        credit_customer_id: form.payment_mode === "CREDIT" ? form.credit_customer_id : null,
        vehicle: form.vehicle || null,
        note: form.note || null,
      });
      toast.success(__t("csa.csaportal.lube_sale_added"));
      setForm({ lube_item_id: "", item_name: "", qty: "", unit_price: "", payment_mode: "CASH", credit_customer_id: "", vehicle: "", note: "" });
      onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/hisab/draft/lube-sale/${id}`);
      toast.success(__t("csa.csaportal.removed"));
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="lube-tab">
      <div className="rounded-lg border border-border p-3">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{__t("csa.csaportal.add_lube_sale")}</div>
        <div className="grid gap-2 md:grid-cols-2">
          <div>
            <Label className="text-xs">{__t("csa.product")}</Label>
            {lubeItems.length > 0 ? (
              <select
                value={form.lube_item_id}
                onChange={(e) => onPickItem(e.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-border bg-background px-2 text-sm text-foreground"
                data-testid="lube-item-select"
              >
                <option value="">— Select —</option>
                {lubeItems.map((i) => (
                  <option key={i.id} value={i.id}>{i.name} · Rs. {Number(i.unit_price || 0).toLocaleString("en-IN")}/{i.unit}</option>
                ))}
              </select>
            ) : (
              <Input
                value={form.item_name}
                onChange={(e) => setForm({ ...form, item_name: e.target.value })}
                placeholder={__t("csa.csaportal.lube_name_dealer_hasn_t_set_catalog")}
                className="h-10 text-sm"
                data-testid="lube-item-name"
              />
            )}
          </div>
          <div>
            <Label className="text-xs">{__t("csa.csaportal.quantity")}</Label>
            <Input
              inputMode="decimal"
              value={form.qty}
              onChange={(e) => setForm({ ...form, qty: e.target.value.replace(/[^0-9.]/g, "") })}
              placeholder="e.g. 1"
              className="h-10 text-sm"
              data-testid="lube-qty"
            />
          </div>
          <div>
            <Label className="text-xs">{__t("csa.csaportal.unit_price_rs")}</Label>
            <Input
              inputMode="decimal"
              value={form.unit_price}
              onChange={(e) => setForm({ ...form, unit_price: e.target.value.replace(/[^0-9.]/g, "") })}
              placeholder="e.g. 550"
              className="h-10 text-sm"
              data-testid="lube-price"
            />
          </div>
          <div>
            <Label className="text-xs">{__t("csa.csaportal.payment_mode")}</Label>
            <select
              value={form.payment_mode}
              onChange={(e) => setForm({ ...form, payment_mode: e.target.value })}
              className="mt-1 h-10 w-full rounded-md border border-border bg-background px-2 text-sm text-foreground"
              data-testid="lube-payment-mode"
            >
              <option value="CASH">{__t("csa.csaportal.cash")}</option>
              <option value="UPI">{__t("csa.csaportal.upi_online")}</option>
              <option value="CREDIT">Credit (customer)</option>
            </select>
          </div>
          {form.payment_mode === "CREDIT" && (
            <div className="md:col-span-2">
              <Label className="text-xs">{__t("csa.csaportal.credit_customer")}</Label>
              <select
                value={form.credit_customer_id}
                onChange={(e) => setForm({ ...form, credit_customer_id: e.target.value })}
                className="mt-1 h-10 w-full rounded-md border border-border bg-background px-2 text-sm text-foreground"
                data-testid="lube-credit-customer"
              >
                <option value="">— Select customer —</option>
                {creditCustomers.filter((c) => c.active !== false).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}{c.phone ? " · " + c.phone : ""}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <Label className="text-xs">{__t("csa.csaportal.vehicle_no_optional")}</Label>
            <Input
              value={form.vehicle}
              onChange={(e) => setForm({ ...form, vehicle: e.target.value.toUpperCase() })}
              placeholder={__t("csa.csaportal.mh12ab1234")}
              className="h-10 text-sm"
              data-testid="lube-vehicle"
            />
          </div>
          <div>
            <Label className="text-xs">{__t("csa.csaportal.note_optional")}</Label>
            <Input
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="h-10 text-sm"
              data-testid="lube-note"
            />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            Line total: <b className="font-mono text-foreground">Rs. {(Number(form.qty || 0) * Number(form.unit_price || 0)).toLocaleString("en-IN")}</b>
          </div>
          <Button size="sm" onClick={submit} disabled={saving} data-testid="lube-add-submit">
            <Plus className="mr-1 h-3.5 w-3.5" /> {saving ? "Saving…" : "Add lube sale"}
          </Button>
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">This shift&apos;s lube sales</div>
          {total > 0 && (
            <div className="text-xs">
              Total: <b className="font-mono">Rs. {total.toLocaleString("en-IN")}</b>
            </div>
          )}
        </div>
        {entries.length === 0 ? (
          <div className="rounded border border-dashed border-border py-4 text-center text-xs text-muted-foreground" data-testid="lube-empty">
            No lube sales yet. Add one above.
          </div>
        ) : (
          <div className="divide-y divide-border overflow-hidden rounded-md border border-border" data-testid="lube-list">
            {entries.map((e) => (
              <div key={e.id} className="flex items-center justify-between p-2 text-sm" data-testid={`lube-entry-${e.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-foreground">
                    {e.item_name || "Lube"} · <span className="font-mono">{e.qty}</span> × Rs. {e.unit_price}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[9px] font-semibold uppercase text-foreground/80">{e.payment_mode}</span>
                    {e.customer_name && " · " + e.customer_name}
                    {e.vehicle && " · " + e.vehicle}
                    {e.note && " · " + e.note}
                  </div>
                </div>
                <div className="ml-2 flex items-center gap-2">
                  <div className="font-mono text-sm tabular-nums text-foreground">Rs. {Number(e.amount).toLocaleString("en-IN")}</div>
                  <button
                    onClick={() => remove(e.id)}
                    className="rounded p-1 text-muted-foreground hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40"
                    data-testid={`lube-remove-${e.id}`}
                    title={__t("csa.csaportal.remove")}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function ExpensesTab({ draft, onReload }) {
  const { t: __t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [customHeads, setCustomHeads] = useState([]);
  const [form, setForm] = useState({
    category: "MISC", amount: "", description: "", vendor: "",
    gst_amount: "", payment_mode: "CASH", reference: "",
  });
  const [photo, setPhoto] = useState(null);

  useEffect(() => {
    api.get("/expenses/categories").then((r) => {
      setCategories(r.data.categories || []);
      setCustomHeads(r.data.custom_heads || []);
    }).catch(() => {});
  }, []);

  const readFile = (file) => {
    if (!file) return;
    const r = new FileReader(); r.onload = () => setPhoto(r.result); r.readAsDataURL(file);
  };
  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setNum = (k, v) => setField(k, String(v ?? "").replace(/[^0-9.]/g, ""));

  const add = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error(__t("csa.csaportal.enter_a_valid_amount"));
    if (!form.description.trim()) return toast.error(__t("csa.csaportal.description_is_required"));
    try {
      await api.post("/hisab/draft/expense", {
        category: form.category,
        amount: Number(form.amount),
        description: form.description.trim(),
        vendor: form.vendor || null,
        gst_amount: form.gst_amount ? Number(form.gst_amount) : null,
        payment_mode: form.payment_mode,
        reference: form.reference || null,
        photo_base64: photo,
      });
      setForm({ category: form.category, amount: "", description: "", vendor: "", gst_amount: "", payment_mode: form.payment_mode, reference: "" });
      setPhoto(null);
      toast.success(__t("csa.csaportal.expense_added")); onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const catLabel = (code) => categories.find((c) => c.code === code)?.label || code;

  return (
    <div className="space-y-4" data-testid="expenses-tab">
      <div className="rounded-xl border border-border bg-card/40 p-3 sm:p-4">
        <div className="mb-3 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Receipt className="h-4 w-4" />
          </div>
          <div>
            <div className="text-sm font-bold text-foreground">{__t("csa.add_expense")}</div>
            <div className="text-[11px] text-muted-foreground">Only cash entries reduce your shift&apos;s cash-expected.</div>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Category *</Label>
            <Select value={form.category} onValueChange={(v) => setField("category", v)}>
              <SelectTrigger data-testid="expense-category" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                ))}
                {customHeads.length > 0 && customHeads.map((h) => (
                  <SelectItem key={h.code} value={h.code}>{h.label} (custom)</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Amount (Rs.) *</Label>
            <Input inputMode="decimal" type="text" pattern="[0-9]*\.?[0-9]*"
              value={form.amount} onChange={(e) => setNum("amount", e.target.value)}
              data-testid="expense-amount" className="h-11 font-mono text-base tabular-nums font-semibold" placeholder="0.00" />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Description *</Label>
            <Input value={form.description} onChange={(e) => setField("description", e.target.value)} data-testid="expense-desc" className="h-11" placeholder={__t("csa.csaportal.short_note_what_was_this_expense_for")} />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{__t("csa.csaportal.vendor_paid_to")}</Label>
            <Input value={form.vendor} onChange={(e) => setField("vendor", e.target.value)} data-testid="expense-vendor" className="h-11" placeholder="e.g. HP Petroleum" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{__t("csa.csaportal.gst_amount_if_any")}</Label>
            <Input inputMode="decimal" type="text" pattern="[0-9]*\.?[0-9]*"
              value={form.gst_amount} onChange={(e) => setNum("gst_amount", e.target.value)}
              data-testid="expense-gst" className="h-11" placeholder="0.00" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{__t("csa.csaportal.payment_mode")}</Label>
            <Select value={form.payment_mode} onValueChange={(v) => setField("payment_mode", v)}>
              <SelectTrigger data-testid="expense-paymode" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="CASH">{__t("csa.csaportal.cash")}</SelectItem>
                <SelectItem value="UPI">{__t("csa.csaportal.upi")}</SelectItem>
                <SelectItem value="CARD">{__t("csa.csaportal.card")}</SelectItem>
                <SelectItem value="BANK">{__t("csa.csaportal.bank_transfer")}</SelectItem>
                <SelectItem value="CHEQUE">{__t("csa.csaportal.cheque")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{__t("csa.csaportal.reference_txn_bill_no")}</Label>
            <Input value={form.reference} onChange={(e) => setField("reference", e.target.value)} data-testid="expense-ref" className="h-11" />
          </div>
          <div className="sm:col-span-2">
            <FileField label={__t("csa.csaportal.bill_receipt_photo_optional")} value={photo} onChange={readFile} testId="expense-photo" />
          </div>
        </div>
        <div className={"mt-3 rounded-md px-3 py-2 text-[11px] " + (form.payment_mode === "CASH"
          ? "bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
          : "bg-secondary/60 text-muted-foreground")}
        >
          {form.payment_mode === "CASH"
            ? "Cash — will be deducted from your shift's cash-on-hand."
            : `${form.payment_mode} — logged for accounting only, does not reduce cash on hand.`}
        </div>
        <Button onClick={add} disabled={!form.amount || !form.description} data-testid="expense-add" className="mt-3 h-12 w-full text-base font-bold sm:w-auto sm:px-6">
          <Plus className="h-4 w-4" />{__t("csa.add_expense")}</Button>
      </div>
      <List
        items={draft?.expenses || []}
        labelFn={(e) => `${catLabel(e.category || "MISC")} · ${e.description}${e.payment_mode && e.payment_mode !== "CASH" ? " (" + e.payment_mode + ")" : ""}`}
        amountFn={(e) => e.amount}
        testPrefix="expense-row"
        onDelete={async (e) => {
          await api.delete(`/hisab/draft/expense/${e.id}`);
          toast.success(__t("csa.csaportal.expense_deleted"));
          onReload();
        }}
      />
    </div>
  );
}

function NotesTab({ draft, onReload }) {
  const { t: __t } = useTranslation();
  const [text, setText] = useState(draft?.notes || "");
  useEffect(() => setText(draft?.notes || ""), [draft?.notes]);
  const save = async () => {
    try {
      await api.patch("/hisab/draft/notes", { note: text });
      toast.success(__t("csa.csaportal.notes_saved")); onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-3">
      <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder={__t("csa.csaportal.rough_notes_for_this_shift")} data-testid="shift-notes" />
      <Button onClick={save} data-testid="shift-notes-save">{__t("csa.csaportal.save_notes")}</Button>
    </div>
  );
}

function List({ items, labelFn, amountFn, testPrefix, onDelete }) {
  const { t: __t } = useTranslation();
  if (!items.length) return <p className="pt-2 text-xs text-muted-foreground/70">{__t("csa.csaportal.nothing_yet")}</p>;
  const handleDelete = async (it) => {
    if (!onDelete) return;
    if (!window.confirm(`Delete this entry (${rupee(amountFn(it))})? This can't be undone.`)) return;
    try {
      await onDelete(it);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="divide-y divide-slate-100">
      {items.map((it, i) => (
        <div key={it.id} className="flex items-center justify-between gap-2 py-2 text-sm" data-testid={`${testPrefix}-${i}`}>
          <div className="min-w-0 flex-1 truncate">{labelFn(it)}</div>
          <div className="flex items-center gap-2">
            <div className="font-mono font-semibold">{rupee(amountFn(it))}</div>
            {onDelete && (
              <button
                type="button"
                onClick={() => handleDelete(it)}
                aria-label={__t("csa.csaportal.delete_entry")}
                data-testid={`${testPrefix}-delete-${i}`}
                className="rounded-md p-1.5 text-red-500 transition-colors hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-500/10"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function CloseTab({ summary, onSubmitted }) {
  const { t: __t } = useTranslation();
  const [cash, setCash] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Testing Notes 14 · CSA "Preview before submit" flow. Instead of firing
  // the submit API on button click, we open a review dialog that shows
  // every line the CSA entered (readings, online, credit, lube, expenses,
  // cash reconciliation) with a big Confirm button. Cuts down on
  // rejections + shortage flags because the CSA catches their own errors.
  const [previewOpen, setPreviewOpen] = useState(false);
  const totals = summary?.totals || {};
  const expected = totals.cash_expected ?? 0;
  const collectedSoFar = Number(totals.cash_collected_so_far || 0);
  const collectionsCount = Number(totals.cash_collections_count || 0);
  const residualExpected = Number(totals.cash_residual_expected != null ? totals.cash_residual_expected : expected);
  const totalCashDelivered = collectedSoFar + Number(cash || 0);
  const delta = totalCashDelivered - expected;
  const cashCollections = summary?.cash_collections || [];
  const attendance = summary?.attendance || {};
  const alreadyCombined = attendance?.combined_prev_attendance_id || summary?.draft?.combined === true;

  // Detect nozzle-level completeness (based on server-persisted data only — matches backend logic)
  const activeAllocs = (summary?.allocations || []).filter((a) => !a.released_at);
  const missingOpen = activeAllocs.filter((a) => a.opening == null);
  const missingClose = activeAllocs.filter((a) => a.opening != null && a.closing == null);
  const readyForClose = activeAllocs.length > 0 && missingOpen.length === 0 && missingClose.length === 0;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/hisab/draft/submit", { cash_submitted: Number(cash), closing_note: note });
      clearShiftNotification().catch(() => {});
      setPreviewOpen(false);
      toast.success(__t("csa.csaportal.shift_submitted_to_manager_for_verificat"));
      onSubmitted && onSubmitted(data.hisab_id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {!readyForClose && (
        <div className="rounded-lg border-2 border-red-200 bg-red-50 p-4" data-testid="close-readiness-warning">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-red-900">{__t("csa.csaportal.cannot_close_shift_yet")}</div>
              <ul className="mt-1 space-y-0.5 text-xs text-red-800">
                {activeAllocs.length === 0 && (
                  <li>{__t("csa.csaportal.you_have_no_active_nozzle_allocations_al")}</li>
                )}
                {missingOpen.length > 0 && (
                  <li>
                    <b>{__t("csa.csaportal.opening_reading_missing_on")}</b>{" "}
                    {missingOpen.map((a) => `${a.machine_code}-${a.nozzle_code}`).join(", ")}
                  </li>
                )}
                {missingClose.length > 0 && (
                  <li>
                    <b>{__t("csa.csaportal.closing_reading_missing_on")}</b>{" "}
                    {missingClose.map((a) => `${a.machine_code}-${a.nozzle_code}`).join(", ")}
                  </li>
                )}
                <li className="mt-1 text-red-700">
                  Tip: after typing a value, tab out of the field or tap outside — it auto-saves.
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Tile label={__t("csa.sales")} value={rupee(totals.sales)} />
        <Tile label={__t("setup.online")} value={rupee(totals.online)} />
        <Tile label={__t("manager.kpi_credit")} value={rupee(totals.credit)} />
        <Tile label={__t("csa.csaportal.cash_exp")} value={rupee(totals.cash_expenses ?? totals.expenses)} />
      </div>
      {totals.non_cash_expenses > 0 && (
        <div className="rounded-md bg-secondary px-3 py-2 text-xs text-muted-foreground" data-testid="non-cash-expenses-hint">
          Non-cash expenses (UPI/card/bank/cheque): <b>{rupee(totals.non_cash_expenses)}</b> — logged but not deducted from cash on hand.
        </div>
      )}
      <div className="rounded-lg bg-amber-50 p-4 dark:bg-amber-950/30">
        <div className="mb-1 text-xs uppercase text-amber-800 dark:text-amber-300">{__t("csa.cash_expected")}</div>
        <div className="text-3xl font-bold text-amber-900 dark:text-amber-100">{rupee(expected)}</div>
        <p className="mt-1 text-xs text-amber-700 dark:text-amber-300/80">{__t("csa.csaportal.everything_above_is_auto_calculated")}</p>
      </div>
      {collectionsCount > 0 && (
        <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900/50 dark:bg-emerald-950/30" data-testid="cash-collections-summary">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold uppercase text-emerald-800 dark:text-emerald-300">
                Already handed over to manager
              </div>
              <div className="text-2xl font-bold text-emerald-900 dark:text-emerald-100">{rupee(collectedSoFar)}</div>
              <div className="mt-0.5 text-[11px] text-emerald-700 dark:text-emerald-300/80">
                Across {collectionsCount} handover{collectionsCount === 1 ? "" : "s"} during your shift.
              </div>
            </div>
          </div>
          {cashCollections.length > 0 && (
            <div className="mt-2 divide-y divide-emerald-200/60 text-[11px] text-emerald-900 dark:divide-emerald-800/40 dark:text-emerald-100">
              {cashCollections.map((c) => (
                <div key={c.id} className="flex items-center justify-between py-1" data-testid={`cash-collection-${c.id}`}>
                  <div className="min-w-0 truncate">
                    <span className="font-mono font-semibold">{rupee(c.amount)}</span>
                    {" · "}<span className="opacity-80">{new Date(c.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    {" · to "}<span className="opacity-80">{c.manager_name}</span>
                    {c.note && (<><br /><span className="text-[10px] italic opacity-70">"{c.note}"</span></>)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div>
        <Label>{collectionsCount > 0 ? "Residual cash you're handing over now" : "Physical cash submitted"}</Label>
        {collectionsCount > 0 && (
          <p className="mb-1 text-[11px] text-muted-foreground">
            Manager already collected <b>{rupee(collectedSoFar)}</b>. Enter only the remaining cash still in your hand
            (expected: <b>{rupee(residualExpected)}</b>).
          </p>
        )}
        <Input inputMode="decimal" type="text" pattern="[0-9]*\.?[0-9]*"
          value={cash} onChange={(e) => setCash(e.target.value.replace(/[^0-9.]/g, ""))}
          data-testid="close-cash" className="h-11 md:h-9" />
      </div>
      {cash && (
        <div className={"rounded-lg p-3 text-white " + (delta >= 0 ? "bg-emerald-600" : "bg-red-600")} data-testid="close-delta">
          <div className="text-xs uppercase opacity-80">{delta >= 0 ? "Surplus" : "Shortage"} (all cash: {rupee(totalCashDelivered)} vs expected {rupee(expected)})</div>
          <div className="text-2xl font-bold">{rupee(Math.abs(delta))}</div>
          {delta < 0 && <div className="mt-1 text-xs">{__t("csa.csaportal.this_will_be_added_to_your_monthly_short")}</div>}
        </div>
      )}
      <Textarea rows={3} placeholder={__t("csa.closing_note")} value={note} onChange={(e) => setNote(e.target.value)} data-testid="close-note" />
      {alreadyCombined && (
        <div className="rounded-lg border-2 border-sky-300 bg-sky-50 p-3 text-sm dark:border-sky-800 dark:bg-sky-950/30" data-testid="combined-shift-banner">
          <b className="text-sky-900 dark:text-sky-100">{__t("csa.csaportal.extended_shift")}</b>
          <p className="mt-0.5 text-xs text-sky-800 dark:text-sky-300">
            This HISAB covers <b>{summary?.draft?.shift || "multiple shifts"}</b> today. Submitting now closes it for verification.
          </p>
        </div>
      )}
      <Button
        onClick={() => setPreviewOpen(true)}
        disabled={!cash || !readyForClose}
        className="h-12 w-full text-base"
        data-testid="close-submit"
      >
        {readyForClose ? "Review & Submit" : "Complete all readings first"}
      </Button>

      {/* Testing Notes 14 · Preview dialog — final review before submit. */}
      <HisabPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        summary={summary}
        cash={cash}
        note={note}
        totalCashDelivered={totalCashDelivered}
        delta={delta}
        expected={expected}
        collectedSoFar={collectedSoFar}
        collectionsCount={collectionsCount}
        onConfirm={submit}
        submitting={submitting}
      />
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="rounded-lg bg-secondary p-3">
      <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
      <div className="text-lg font-bold">{value}</div>
    </div>
  );
}

/**
 * HisabPreviewDialog — Testing Notes 14
 * ─────────────────────────────────────
 * Final "review before submit" step for the CSA. Shows every line the
 * CSA entered during the shift (readings, online, credit, lube,
 * expenses, cash reconciliation) with a clear Confirm / Back-to-edit
 * choice. Reduces manager rejections + shortage flags because the CSA
 * catches their own typos before the HISAB goes upstream.
 */
function HisabPreviewDialog({
  open, onOpenChange, summary, cash, note,
  totalCashDelivered, delta, expected, collectedSoFar, collectionsCount,
  onConfirm, submitting,
}) {
  const draft = summary?.draft || {};
  const allocs = (summary?.allocations || []).filter((a) => !a.released_at);
  const online = draft.online_txs || [];
  const credit = draft.credit_entries || [];
  const lube = draft.lube_sales || [];
  const expenses = draft.expenses || [];
  const totals = summary?.totals || {};
  const shiftLabel = draft.shift || draft.shift_id || "Shift";

  const shortageAlert = delta < -500 || (expected > 0 && Math.abs(delta) / expected > 0.005);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] overflow-hidden flex flex-col p-0"
        data-testid="hisab-preview-dialog"
      >
        <DialogHeader className="border-b border-border bg-gradient-to-r from-primary/5 to-transparent p-4">
          <DialogTitle className="text-base font-bold">
            Review your HISAB before submitting
          </DialogTitle>
          <p className="text-[11px] text-muted-foreground">
            Once you submit, only your manager can edit this shift. Please double-check every number.
          </p>
        </DialogHeader>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {/* Sales summary tiles */}
          <PreviewSection title="Shift totals" testid="preview-totals">
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              <PreviewTile label="Sales" value={rupee(totals.sales)} strong />
              <PreviewTile label="Online" value={rupee(totals.online)} />
              <PreviewTile label="Credit" value={rupee(totals.credit)} />
              <PreviewTile label="Expenses" value={rupee(totals.cash_expenses ?? totals.expenses)} />
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground">
              Shift: <b className="text-foreground">{shiftLabel}</b>
              {draft.date && <> · Date: <b className="text-foreground">{draft.date}</b></>}
            </div>
          </PreviewSection>

          {/* Nozzle readings */}
          <PreviewSection title={`Nozzle readings (${allocs.length})`} testid="preview-readings">
            {allocs.length === 0 ? (
              <p className="text-xs italic text-muted-foreground">No active nozzles.</p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-border">
                <table className="w-full text-[11px]">
                  <thead className="bg-muted/50 text-[10px] font-black uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Nozzle</th>
                      <th className="px-2 py-1.5 text-right">Open</th>
                      <th className="px-2 py-1.5 text-right">Close</th>
                      <th className="px-2 py-1.5 text-right">T (L)</th>
                      <th className="px-2 py-1.5 text-right">Net L</th>
                      <th className="px-2 py-1.5 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border font-mono tabular-nums">
                    {allocs.map((a) => {
                      const open = Number(a.opening || 0);
                      const close = Number(a.closing || 0);
                      const test = Number(a.testing || 0);
                      const net = Math.max(0, close - open - test);
                      const amt = net * Number(a.rate || 0);
                      return (
                        <tr key={a.id} data-testid={`preview-reading-${a.id}`}>
                          <td className="px-2 py-1.5 font-sans">
                            <b>{a.machine_code}·{a.nozzle_code}</b>
                            <span className="ml-1 text-[10px] text-muted-foreground">{a.product}</span>
                          </td>
                          <td className="px-2 py-1.5 text-right">{open.toFixed(2)}</td>
                          <td className="px-2 py-1.5 text-right">{close.toFixed(2)}</td>
                          <td className="px-2 py-1.5 text-right text-amber-700">{test.toFixed(2)}</td>
                          <td className="px-2 py-1.5 text-right font-semibold">{net.toFixed(2)}</td>
                          <td className="px-2 py-1.5 text-right font-semibold">₹{amt.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </PreviewSection>

          {/* Online transactions */}
          <PreviewSection title={`Online transactions (${online.length})`} testid="preview-online">
            {online.length === 0
              ? <p className="text-xs italic text-muted-foreground">No online transactions.</p>
              : (
                <ul className="space-y-1 text-[12px]">
                  {online.map((o, i) => (
                    <li key={o.id || i} className="flex items-center justify-between rounded-md bg-muted/30 px-2 py-1.5" data-testid={`preview-online-${o.id || i}`}>
                      <span className="min-w-0 truncate">
                        <b>{o.mode || "UPI"}</b>
                        {o.provider && <> · {o.provider}</>}
                        {o.reference && <span className="text-[10px] text-muted-foreground"> · Ref {o.reference}</span>}
                      </span>
                      <span className="font-mono font-bold tabular-nums">{rupee(o.amount)}</span>
                    </li>
                  ))}
                </ul>
              )
            }
          </PreviewSection>

          {/* Credit sales */}
          <PreviewSection title={`Credit sales (${credit.length})`} testid="preview-credit">
            {credit.length === 0
              ? <p className="text-xs italic text-muted-foreground">No credit sales.</p>
              : (
                <ul className="space-y-1 text-[12px]">
                  {credit.map((c, i) => (
                    <li key={c.id || i} className="flex items-center justify-between rounded-md bg-muted/30 px-2 py-1.5" data-testid={`preview-credit-${c.id || i}`}>
                      <span className="min-w-0 truncate">
                        <b>{c.customer_name || "Customer"}</b>
                        {c.vehicle && <span className="text-[10px] text-muted-foreground"> · {c.vehicle}</span>}
                        {c.driver && <span className="text-[10px] text-muted-foreground"> · {c.driver}</span>}
                      </span>
                      <span className="font-mono font-bold tabular-nums">{rupee(c.amount)}</span>
                    </li>
                  ))}
                </ul>
              )
            }
          </PreviewSection>

          {/* Lube sales */}
          {lube.length > 0 && (
            <PreviewSection title={`Lube sales (${lube.length})`} testid="preview-lube">
              <ul className="space-y-1 text-[12px]">
                {lube.map((l, i) => (
                  <li key={l.id || i} className="flex items-center justify-between rounded-md bg-muted/30 px-2 py-1.5" data-testid={`preview-lube-${l.id || i}`}>
                    <span className="min-w-0 truncate">
                      <b>{l.brand || l.name || "Lube"}</b>
                      {l.pack_size && <span className="text-[10px] text-muted-foreground"> · {l.pack_size}</span>}
                      {l.qty && <span className="text-[10px] text-muted-foreground"> · qty {l.qty}</span>}
                    </span>
                    <span className="font-mono font-bold tabular-nums">{rupee(l.amount)}</span>
                  </li>
                ))}
              </ul>
            </PreviewSection>
          )}

          {/* Expenses */}
          <PreviewSection title={`Expenses (${expenses.length})`} testid="preview-expenses">
            {expenses.length === 0
              ? <p className="text-xs italic text-muted-foreground">No expenses.</p>
              : (
                <ul className="space-y-1 text-[12px]">
                  {expenses.map((e, i) => (
                    <li key={e.id || i} className="flex items-center justify-between rounded-md bg-muted/30 px-2 py-1.5" data-testid={`preview-expense-${e.id || i}`}>
                      <span className="min-w-0 truncate">
                        <b>{e.head || "Expense"}</b>
                        {e.mode && <span className="text-[10px] text-muted-foreground"> · {e.mode}</span>}
                        {e.note && <span className="text-[10px] italic text-muted-foreground"> · "{e.note}"</span>}
                      </span>
                      <span className="font-mono font-bold tabular-nums">{rupee(e.amount)}</span>
                    </li>
                  ))}
                </ul>
              )
            }
          </PreviewSection>

          {/* Cash reconciliation */}
          <PreviewSection title="Cash reconciliation" testid="preview-cash">
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
              <PreviewTile label="Expected" value={rupee(expected)} />
              {collectionsCount > 0 && (
                <PreviewTile label={`Handed over (${collectionsCount})`} value={rupee(collectedSoFar)} tone="emerald" />
              )}
              <PreviewTile label="Cash on hand now" value={rupee(cash || 0)} tone="emerald" />
              <PreviewTile label="Total delivered" value={rupee(totalCashDelivered)} strong />
              <PreviewTile
                label={delta >= 0 ? "Surplus" : "Shortage"}
                value={rupee(Math.abs(delta))}
                tone={delta === 0 ? "emerald" : delta > 0 ? "amber" : "rose"}
              />
            </div>
            {shortageAlert && delta !== 0 && (
              <div
                className={
                  "mt-2 rounded-md border p-2 text-[11px] " +
                  (delta < 0
                    ? "border-rose-300 bg-rose-50 text-rose-900"
                    : "border-amber-200 bg-amber-50 text-amber-900")
                }
                data-testid="preview-cash-alert"
              >
                {delta < 0 ? "⚠ " : "• "}
                {delta < 0 ? "Shortage" : "Surplus"} of{" "}
                <b>{rupee(Math.abs(delta))}</b>. Manager will see this flag.
                {delta < 0 && " Double-check your cash count before submitting."}
              </div>
            )}
          </PreviewSection>

          {/* Note */}
          {note && (
            <PreviewSection title="Your note to manager" testid="preview-note">
              <p className="rounded-md bg-muted/30 px-3 py-2 text-[12px] italic text-foreground">
                "{note}"
              </p>
            </PreviewSection>
          )}
        </div>

        <DialogFooter className="flex-row gap-2 border-t border-border bg-muted/20 p-3 sm:justify-between">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="preview-back-btn"
          >
            ← Back to edit
          </Button>
          <Button
            onClick={onConfirm}
            disabled={submitting}
            className="min-w-[180px]"
            data-testid="preview-confirm-btn"
          >
            {submitting ? "Submitting…" : "✓ Confirm & Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewSection({ title, children, testid }) {
  return (
    <div className="rounded-lg border border-border bg-card/50 p-3" data-testid={testid}>
      <div className="mb-2 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

function PreviewTile({ label, value, tone, strong }) {
  const toneCls =
    tone === "emerald"
      ? "text-emerald-700 dark:text-emerald-400"
      : tone === "amber"
      ? "text-amber-700 dark:text-amber-400"
      : tone === "rose"
      ? "text-rose-700 dark:text-rose-400"
      : "text-foreground";
  return (
    <div className="rounded-md bg-muted/30 px-3 py-2">
      <div className="text-[9px] font-black uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className={"font-mono tabular-nums " + (strong ? "text-base font-black " : "text-sm font-bold ") + toneCls}>
        {value}
      </div>
    </div>
  );
}
